import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, FormControl, FormControlLabel, FormHelperText, IconButton, InputLabel, LinearProgress, MenuItem, Select, Tab, Tabs, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo,useRef, useState } from "react";
import Datagrid from "../../components/Datagrid/Datagrid";
import DatagridLoading from "../../components/DatagridLoading/DatagridLoading";

import { AddCircle } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
// import { getDocumentCategories, getDocumentsMetaData, uploadDocument, updateDocument, deleteDocument, searchMachines as searchMachinesApi, getDocumentAudience, createDocumentCategory, updateDocumentCategory, deleteDocumentCategory, updateCategoryDocuments } from "../../services/documentService";
import RichTextEditor from "../../components/RichTextEditor/RichTextEditor";
import FileDropZone from "../../components/FileDropZone/FileDropZone";
import { formatMachineNumberFromRow } from "../../utilities/machineDisplay";
import Swal from "sweetalert2";
import DocCard from "../../components/DocCard/DocCard";
import { AddCategoryModal, CategoryViewModal, DeleteCategoryModal, EditCategoryModal } from "../../components/DocumentCategoryModals";
import DocumentViewModal from "../../components/DocumentViewModal";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import NotAuthorized from "../../components/NotAuthorized/NotAuthorized";
import { SERVER_BASE_URL } from "../../config";
import { useAuth } from "../../context/authContext";
import { useSite } from "../../context/siteContext";
import { getMachinesByIds } from "../../services/breadCrumbsService";
import { createDocumentCategory, deleteDocument, deleteDocumentCategory, getDocumentAudience, getDocumentCategories, getDocumentsMetaData, getDocumentUploadUrl, searchMachines as searchMachinesApi, updateCategoryDocuments, updateDocument, updateDocumentCategory, uploadDocument } from "../../services/documentService";
import { getUserAllocations } from "../../services/manageSitesService";

const INITIAL_DOCUMENT = {
    name: "",
    description: "",
    attachment_type: "file",
    url: "",
    files: [],
    status: "private",
};

const RequiredLabel = ({ children }) => (
    <>
        {children}
        <Box component="span" sx={{ color: "error.main" }}>
            {" *"}
        </Box>
    </>
);

const DEFAULT_CATEGORY_ID = 1;
const getBulkFileKey = (file) => `${file?.name || "file"}-${file?.size || 0}-${file?.lastModified || 0}`;
const createBlankUrlRow = (index = 0) => ({
    key: `url-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 6)}`,
    name: "",
    description: "",
    url: "",
});
const normalizeDocumentNameKey = (value = "") => String(value || "").trim().toLowerCase();

const formatFileSize = (bytes = 0) => {
    if (!bytes) return "0 MB";
    const mb = bytes / (1024 * 1024);
    if (mb < 1024) return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
};

const getFileBaseName = (fileName = "") =>
    String(fileName || "document").replace(/\.[^/.]+$/, "") || "document";

const getDocumentFileName = (attachment, fallbackName = "") =>
    String(attachment || "").split("/").pop() ||
    String(fallbackName || "").split("/").pop() ||
    "document";

const getDocumentFileUrl = (attachment, fallbackName = "") =>
    `${SERVER_BASE_URL}/assets/Documents/${encodeURIComponent(getDocumentFileName(attachment, fallbackName))}`;

const getFileExtension = (fileName = "") => {
    const normalized = String(fileName || "").trim();
    if (!normalized) return "";
    const dotIndex = normalized.lastIndexOf(".");
    return dotIndex >= 0 ? normalized.slice(dotIndex + 1).toLowerCase() : "";
};

const getFileTypeBadgeLabel = (fileName = "") => {
    const ext = getFileExtension(fileName);
    if (!ext) return "FILE";
    const compact = ext.replace(/[^a-z0-9]/gi, "");
    if (compact === "editorconfig") return "EDIT";
    if (compact.length <= 5) return compact.toUpperCase();
    return compact.slice(0, 4).toUpperCase();
};

const getFileTypeBadgeColors = (fileName = "") => {
    const ext = getFileExtension(fileName);
    if (["pdf"].includes(ext)) return { backgroundColor: "#fdecec", color: "#c62828" };
    if (["doc", "docx"].includes(ext)) return { backgroundColor: "#e8f0fe", color: "#1565c0" };
    if (["xls", "xlsx", "csv"].includes(ext)) return { backgroundColor: "#e8f5e9", color: "#2e7d32" };
    if (["jpg", "jpeg", "bmp", "png", "svg"].includes(ext)) return { backgroundColor: "#fff3e0", color: "#ef6c00" };
    if (["mp4"].includes(ext)) return { backgroundColor: "#f3e5f5", color: "#7b1fa2" };
    if (["zip", "bin", "dll", "cache", "pdb", "up2date"].includes(ext)) return { backgroundColor: "#eceff1", color: "#455a64" };
    if (["xml", "json", "yaml", "props", "cs", "txt", "editorconfig"].includes(ext)) return { backgroundColor: "#e0f7fa", color: "#006064" };
    if (["sqlite", "db", "dbold", "bie", "db-shm", "db-wal"].includes(ext)) return { backgroundColor: "#ede7f6", color: "#5e35b1" };
    if (["apa", "mer", "acd", "optix"].includes(ext)) return { backgroundColor: "#fff8e1", color: "#a66a00" };
    return { backgroundColor: "#e6f3ff", color: "#1976d2" };
};

const getFileTypeBadgeFontSize = (label = "") => {
    if (label.length >= 5) return 7.5;
    if (label.length === 4) return 8.5;
    return 10;
};
const escapeHtml = (value = "") => {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
};

const getBooleanOption = (value, fallback = false) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["true", "1", "yes", "on", "enabled"].includes(normalized)) return true;
        if (["false", "0", "no", "off", "disabled"].includes(normalized)) return false;
    }
    return fallback;
};

const getAudienceLocationRowId = (cusId, siteId) => `${Number(cusId)}:${Number(siteId)}`;
const CategoryChipsCell = ({ cats, rowId, noCategoryLabel, compactLimit = 4, colWidth = 300 }) => {
    const containerRef = useRef(null);
    const [cellWidth, setCellWidth] = useState(0);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;

        const updateWidth = () => setCellWidth(el.clientWidth || 0);
        updateWidth();

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", updateWidth);
            return () => window.removeEventListener("resize", updateWidth);
        }

        const observer = new ResizeObserver(() => updateWidth());
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    if (!Array.isArray(cats) || cats.length === 0) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", flexWrap: "wrap", height: "100%" }}>
                <Typography variant="body2" color="text.secondary">{noCategoryLabel}</Typography>
            </Box>
        );
    }

    const effectiveWidth = Math.max(Number(cellWidth || 0), Number(colWidth || 0));
    const baseWidth = 300;
    const perChipWidth = 70;
    const extraVisible = Math.max(0, Math.floor((effectiveWidth - baseWidth) / perChipWidth));
    const visibleCount = Math.min(cats.length, compactLimit + extraVisible);
    const visibleCats = cats.slice(0, visibleCount);
    const remainingCount = Math.max(0, cats.length - visibleCount);

    return (
        <Box
            ref={containerRef}
            sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                height: "100%",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    columnGap: 0.5,
                    width: "100%",
                    minWidth: 0,
                    overflow: "hidden",
                }}
            >
                {visibleCats.map((cat) => (
                    <Chip
                        key={`${rowId}-${cat.category_id || cat.id}`}
                        size="small"
                        label={cat.category_name}
                        sx={{ flexShrink: 1, minWidth: 0, maxWidth: 140 }}
                    />
                ))}
                {remainingCount > 0 && (
                    <Chip size="small" label={`+${remainingCount}`} variant="outlined" sx={{ flexShrink: 0 }} />
                )}
            </Box>
        </Box>
    );
};

const getUploadResultStatus = (value) => {
    if (value === true || value === 1) return "success";
    if (value === false || value === 0) return "failed";
    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["true", "success", "succeeded", "completed", "complete", "uploaded", "ok"].includes(normalized)) return "success";
        if (["false", "fail", "failed", "failure", "error", "errored"].includes(normalized)) return "failed";
    }
    return "unknown";
};

const getUploadResultName = (row = {}, fallback = "") =>
    row?.document_name || row?.file_name || row?.name || row?.attachment || fallback;

const normalizeUploadErrorMessage = (message = "", fallback = "Upload failed") => {
    const raw = String(message || "").trim();
    const normalized = raw.toLowerCase();

    if (normalized.includes("value too long for type character varying(45)") || normalized.includes("value too long for type character varying")) {
        return "The document name is too long. Please shorten it and try again.";
    }

    return raw || fallback;
};

const buildBatchUploadReportHtml = (batch = {}, results = [], options = {}) => {
    const rows = Array.isArray(results) ? results : [];
    const itemLabel = options.itemLabel || "File";
    const rowSuccessCount = rows.filter((row) => getUploadResultStatus(row?.status) === "success").length;
    const rowFailureCount = rows.filter((row) => getUploadResultStatus(row?.status) === "failed").length;
    const successCount = Number(batch?.success_count ?? rowSuccessCount ?? 0);
    const failureCount = Number(batch?.failure_count ?? rowFailureCount ?? 0);
    const totalCount = Number(batch?.total_files ?? rows.length ?? successCount + failureCount ?? 0);
    const completedCount = successCount + failureCount;
    const statusLabel = failureCount > 0
        ? "Completed with some failures"
        : "Completed successfully";

    const tableRows = rows.length > 0
        ? rows.map((row, index) => {
            const isSuccess = getUploadResultStatus(row?.status) === "success";
            const fileName = getUploadResultName(row, `${itemLabel} ${index + 1}`);
            const message = isSuccess
                ? "Uploaded successfully"
                : normalizeUploadErrorMessage(row?.error_message || row?.message);
            const statusChip = isSuccess
                ? '<span style="display:inline-flex;align-items:center;justify-content:center;min-width:72px;padding:4px 10px;border-radius:999px;background:#e7f7ee;color:#1b7f43;font-weight:600;font-size:12px;white-space:nowrap;word-break:normal;overflow-wrap:normal;">Success</span>'
                : '<span style="display:inline-flex;align-items:center;justify-content:center;min-width:72px;padding:4px 10px;border-radius:999px;background:#fdecec;color:#b42318;font-weight:600;font-size:12px;white-space:nowrap;word-break:normal;overflow-wrap:normal;">Failed</span>';
            return `
                <tr>
                    <td style="padding:10px 12px;border-bottom:1px solid #eee;vertical-align:top;word-break:break-word;overflow-wrap:anywhere;">${escapeHtml(fileName)}</td>
                    <td style="padding:10px 12px;border-bottom:1px solid #eee;vertical-align:top;white-space:nowrap;word-break:normal;overflow-wrap:normal;">${statusChip}</td>
                    <td style="padding:10px 12px;border-bottom:1px solid #eee;color:${isSuccess ? "#1b7f43" : "#b42318"};vertical-align:top;word-break:normal;overflow-wrap:break-word;">${escapeHtml(message)}</td>
                </tr>
            `;
        }).join("")
        : `
            <tr>
                <td style="padding:12px;border-bottom:1px solid #eee;vertical-align:top;word-break:break-word;overflow-wrap:anywhere;">Upload summary</td>
                <td style="padding:12px;border-bottom:1px solid #eee;vertical-align:top;white-space:nowrap;word-break:normal;overflow-wrap:normal;">
                    <span style="display:inline-flex;align-items:center;justify-content:center;min-width:72px;padding:4px 10px;border-radius:999px;background:${failureCount > 0 ? "#fdecec" : "#e7f7ee"};color:${failureCount > 0 ? "#b42318" : "#1b7f43"};font-weight:600;font-size:12px;white-space:nowrap;word-break:normal;overflow-wrap:normal;">${failureCount > 0 ? "Failed" : "Success"}</span>
                </td>
                <td style="padding:12px;border-bottom:1px solid #eee;color:${failureCount > 0 ? "#b42318" : "#1b7f43"};vertical-align:top;word-break:normal;overflow-wrap:break-word;">${failureCount > 0 ? "Upload completed with no per-item results returned." : "Uploaded successfully"}</td>
            </tr>
        `;

    return `
        <div style="text-align:left;">
            <p style="margin:0 0 14px 0;color:#555;">
                ${escapeHtml(statusLabel)}. ${completedCount}/${totalCount} processed, ${successCount} succeeded, ${failureCount} failed.
            </p>
            <div style="max-height:360px;overflow:auto;border:1px solid #e5e7eb;border-radius:8px;">
                <table style="width:100%;border-collapse:collapse;font-size:14px;table-layout:fixed;">
                    <colgroup>
                        <col style="width:62%;" />
                        <col style="width:14%;" />
                        <col style="width:24%;" />
                    </colgroup>
                    <thead>
                        <tr style="background:#f8fafc;">
                            <th style="text-align:left;padding:10px 12px;border-bottom:1px solid #e5e7eb;">${escapeHtml(itemLabel)}</th>
                            <th style="text-align:left;padding:10px 12px;border-bottom:1px solid #e5e7eb;white-space:nowrap;word-break:normal;overflow-wrap:normal;">Status</th>
                            <th style="text-align:left;padding:10px 12px;border-bottom:1px solid #e5e7eb;white-space:nowrap;word-break:normal;overflow-wrap:normal;">Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${tableRows}
                    </tbody>
                </table>
            </div>
        </div>
    `;
};

const shouldShowBatchUploadReport = (selectedCount, batchData, results = []) => {
    const totalFiles = Number(batchData?.total_files || 0);
    return Number(selectedCount || 0) > 1 || (Array.isArray(results) && results.length > 1) || totalFiles > 1;
};

const ManageDocuments = () => {
    const { t } = useTranslation();
    const [documents, setDocuments] = useState([]);
    const [categories, setCategories] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(true);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [machines, setMachines] = useState([]);
    const [machineSearch, setMachineSearch] = useState("");
    const [machinePage, setMachinePage] = useState(1);
    const [machineHasMore, setMachineHasMore] = useState(true);
    const [machinesLoading, setMachinesLoading] = useState(false);
    const [newDoc, setNewDoc] = useState(INITIAL_DOCUMENT);
    const [selectedMachines, setSelectedMachines] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editDocId, setEditDocId] = useState(null);
    const [error, setError] = useState({});
    const [formErrors, setFormErrors] = useState([]); // alert errors
    const [visibilityScope, setVisibilityScope] = useState("all");
    const [audienceOptions, setAudienceOptions] = useState([]);
    const [audienceLoading, setAudienceLoading] = useState(false);
    const [isSubmittingDocument, setIsSubmittingDocument] = useState(false);
    const [selectedAudienceCustomers, setSelectedAudienceCustomers] = useState([]);
    const [selectedAudienceSites, setSelectedAudienceSites] = useState([]);
    const [selectedDocCategories, setSelectedDocCategories] = useState([]);
    const [tabIndex, setTabIndex] = useState(0);
    const [uploadTabIndex, setUploadTabIndex] = useState(0);
    const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
    const [editCategoryModal, setEditCategoryModal] = useState({ open: false, category: null });
    const [assignModal, setAssignModal] = useState({ open: false, category: null, documentIds: [] });
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewDoc, setViewDoc] = useState(null);
    const [categoryDetailsModal, setCategoryDetailsModal] = useState({ open: false, category: null, documents: [] });
    const [deleteCategoryModalState, setDeleteCategoryModalState] = useState({ open: false, category: null });
    const [bulkFileDetails, setBulkFileDetails] = useState({});
    const [urlDocumentRows, setUrlDocumentRows] = useState([createBlankUrlRow()]);
    const [validationErrors, setValidationErrors] = useState({});
    const selectedFilesTableRef = useRef(null);
    const previousSelectedFileCountRef = useRef(0);
    const uploadModalScrollRef = useRef(null);
    const [uploadNotificationsEnabled, setUploadNotificationsEnabled] = useState(false);
    const [uploadProgressItems, setUploadProgressItems] = useState([]);
    const [uploadPhase, setUploadPhase] = useState("idle");
    const retryBatchContextRef = useRef(null);
    const { selectedSite, siteData } = useSite();

    const { hasPermission, user } = useAuth();
    const actorRoleType = useMemo(
        () => String(user?.role?.role_type || user?.role?.role_name || "").trim().toLowerCase(),
        [user?.role?.role_type, user?.role?.role_name]
    );
    const [formSiteNumber, setFormSiteNumber] = useState(selectedSite || "");
    const [fileDropzoneKey, setFileDropzoneKey] = useState(0);
    const [fallbackActorSiteIds, setFallbackActorSiteIds] = useState([]);
    const actorSiteIds = useMemo(() => {
        const fromAllocs = Array.isArray(user?.allocations)
            ? user.allocations.map((a) => Number(a?.site_id)).filter((id) => Number.isFinite(id) && id > 0)
            : [];
        return Array.from(new Set(fromAllocs));
    }, [user?.allocations]);
    useEffect(() => {
        let mounted = true;
        const loadFallbackActorScope = async () => {
            if (Number(user?.user_id) === 1 || actorSiteIds.length > 0 || !user?.user_id) {
                if (mounted) setFallbackActorSiteIds([]);
                return;
            }
            try {
                const resp = await getUserAllocations(user.user_id);
                const allocations = Array.isArray(resp?.data?.allocations) ? resp.data.allocations : [];
                const ids = Array.from(
                    new Set(
                        allocations
                            .map((a) => Number(a?.site_id))
                            .filter((id) => Number.isFinite(id) && id > 0)
                    )
                );
                if (mounted) setFallbackActorSiteIds(ids);
            } catch (_) {
                if (mounted) setFallbackActorSiteIds([]);
            }
        };
        loadFallbackActorScope();
        return () => {
            mounted = false;
        };
    }, [actorSiteIds, user?.user_id]);
    const effectiveActorSiteIds = useMemo(() => {
        if (actorSiteIds.length > 0) return actorSiteIds;
        return fallbackActorSiteIds;
    }, [actorSiteIds, fallbackActorSiteIds]);
    const actorHasAllSiteVisibility = useMemo(
        () =>
            Number(user?.user_id) === 1 ||
            ((actorRoleType === "admin" || getBooleanOption(user?.role?.permissions?.user_management?.manage_all_sites)) &&
                effectiveActorSiteIds.length === 0),
        [user?.user_id, actorRoleType, user?.role?.permissions?.user_management?.manage_all_sites, effectiveActorSiteIds.length]
    );
    const scopedSiteData = useMemo(() => {
        const allSites = Array.isArray(siteData) ? siteData : [];
        if (actorHasAllSiteVisibility) return allSites;
        const allowed = new Set((effectiveActorSiteIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0));
        return allSites.filter((site) => allowed.has(Number(site?.site_id)));
    }, [siteData, actorHasAllSiteVisibility, effectiveActorSiteIds]);
    const allowedSiteIdSet = useMemo(
        () => new Set((scopedSiteData || []).map((site) => Number(site?.site_id)).filter((id) => Number.isFinite(id) && id > 0)),
        [scopedSiteData]
    );
    const selectedSiteRecord = useMemo(
        () =>
            scopedSiteData.find((site) => site.customer_site_number === selectedSite) ||
            scopedSiteData[0] ||
            null,
        [scopedSiteData, selectedSite]
    );
    const selectedSiteId = selectedSiteRecord?.site_id;
    const currentSiteMachines = useMemo(
        () => Array.isArray(selectedSiteRecord?.machines)
            ? selectedSiteRecord.machines
            : Array.isArray(selectedSiteRecord?.machine_details)
                ? selectedSiteRecord.machine_details
                : [],
        [selectedSiteRecord]
    );
    const canCreateDocuments = hasPermission("document_management.create_documents");
    const canEditDocuments = hasPermission("document_management.edit_documents");
    const canDeleteDocuments = hasPermission("document_management.delete_documents");
    const canAssignDocuments = canCreateDocuments || canEditDocuments;
    const canManageDocuments = canCreateDocuments || canEditDocuments || canDeleteDocuments;
    const MAX_TOTAL_UPLOAD_SIZE = 1024 * 1024 * 1024; // 1 GB total
    const uploadRowIconColumn = "56px";
    const uploadRowNameColumn = "minmax(120px, 1fr)";
    const uploadRowDescriptionColumn = "minmax(120px, 1fr)";
    const uploadRowCategoryColumn = "minmax(120px, 1fr)";
    const uploadRowStatusColumn = "minmax(96px, 1fr)";
    const uploadRowDeleteColumn = "40px";
    const uploadRowMachineColumn = "minmax(0, 7fr)";
    const uploadStepOneColumns = `${uploadRowIconColumn} ${uploadRowNameColumn} ${uploadRowDescriptionColumn} ${uploadRowCategoryColumn} ${uploadRowStatusColumn} ${uploadRowDeleteColumn}`;
    const uploadStepTwoColumns = `${uploadRowIconColumn} ${uploadRowNameColumn} ${uploadRowCategoryColumn} ${uploadRowStatusColumn} ${uploadRowMachineColumn}`;
    const selectedDocumentFiles = useMemo(
        () => (newDoc.attachment_type === "file" && Array.isArray(newDoc.files) ? newDoc.files : []),
        [newDoc.attachment_type, newDoc.files]
    );
    const editingDocument = useMemo(
        () => (isEditing ? documents.find((doc) => Number(doc?.id) === Number(editDocId)) || null : null),
        [documents, editDocId, isEditing]
    );
    const uploadRows = useMemo(
        () =>
            isEditing
                ? [
                    {
                        key: `edit-${editDocId || "document"}`,
                        isEditRow: true,
                        name: editingDocument?.name || newDoc.name || "",
                        attachment: editingDocument?.attachment || null,
                        attachment_type: editingDocument?.attachment_type || newDoc.attachment_type || "file",
                    },
                ]
                : newDoc.attachment_type === "url"
                    ? urlDocumentRows
                : selectedDocumentFiles,
        [editDocId, editingDocument, isEditing, newDoc.attachment_type, newDoc.name, selectedDocumentFiles, urlDocumentRows]
    );
    const hasUploadRows = uploadRows.length > 0;
    const showFileUploadStepOneTable = !isEditing && uploadTabIndex === 0 && newDoc.attachment_type === "file" && selectedDocumentFiles.length > 0;
    const showUrlUploadStepOneTable = !isEditing && uploadTabIndex === 0 && newDoc.attachment_type === "url";
    const isBulkDocumentUpload = selectedDocumentFiles.length > 1;
    const totalSelectedFileSize = selectedDocumentFiles.reduce((total, file) => total + (Number(file?.size) || 0), 0);
    const uploadOverallProgress = useMemo(() => {
        if (!Array.isArray(uploadProgressItems) || uploadProgressItems.length === 0) return 0;
        const sum = uploadProgressItems.reduce((total, item) => total + (Number(item?.percent) || 0), 0);
        return Math.max(0, Math.min(100, Math.round(sum / uploadProgressItems.length)));
    }, [uploadProgressItems]);

    useEffect(() => {
        if (!openDialog) {
            previousSelectedFileCountRef.current = 0;
            return;
        }
        const nextCount = selectedDocumentFiles.length;
        const shouldScroll =
            !uploadTabIndex &&
            newDoc.attachment_type === "file" &&
            nextCount > 0 &&
            nextCount > previousSelectedFileCountRef.current;

        previousSelectedFileCountRef.current = nextCount;
        if (!shouldScroll) return;

        const timer = window.setTimeout(() => {
            selectedFilesTableRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }, 120);

        return () => window.clearTimeout(timer);
    }, [newDoc.attachment_type, openDialog, selectedDocumentFiles.length, uploadTabIndex]);

    useEffect(() => {
        if (!openDialog) return;
        if (newDoc.attachment_type !== "url") return;
        if (Array.isArray(urlDocumentRows) && urlDocumentRows.length > 0) return;
        setUrlDocumentRows([createBlankUrlRow()]);
    }, [openDialog, newDoc.attachment_type, urlDocumentRows]);

    useEffect(() => {
        if (openDialog) return;
        setFormSiteNumber(selectedSite || "");
    }, [selectedSite, openDialog]);

    useEffect(() => {
        const activeKeys = new Set(selectedDocumentFiles.map(getBulkFileKey));
        setBulkFileDetails((prev) => {
            const next = {};
            Object.entries(prev).forEach(([key, value]) => {
                if (activeKeys.has(key)) next[key] = value;
            });
            return next;
        });
        setValidationErrors((prev) => {
            const next = {};
            Object.entries(prev).forEach(([key, value]) => {
                const baseKey = key.endsWith("_category") ? key.slice(0, -9) : key;
                if (activeKeys.has(baseKey)) next[key] = value;
            });
            return next;
        });
    }, [selectedDocumentFiles]);

    useEffect(() => {
        if (!openDialog) return;
        if (formSiteNumber && scopedSiteData.some((site) => site.customer_site_number === formSiteNumber)) return;
        const fallbackSiteNumber = selectedSiteRecord?.customer_site_number || scopedSiteData?.[0]?.customer_site_number || "";
        setFormSiteNumber(fallbackSiteNumber);
    }, [openDialog, formSiteNumber, scopedSiteData, selectedSiteRecord]);


    const aggregatedCustomerSites = useMemo(() => {
        if (!Array.isArray(scopedSiteData)) return [];
        const map = new Map();
        scopedSiteData.forEach((site) => {
            const cusId = Number(site?.cus_id);
            if (!Number.isFinite(cusId)) return;
            const customerName = site.parent_customer_name || site.customer_name || site.customer_site_name || `${t('UI_TEXT.CUSTOMER')} ${cusId}`;
            const customerNumber = site.parent_customer_number || site.customer_number || "";
            const siteEntry = {
                site_id: site.site_id,
                cus_id: cusId,
                customer_site_name: site.customer_site_name || site.address_name_ln || site.site_name || "",
                customer_site_number: site.customer_site_number,
            };
            if (!map.has(cusId)) {
                map.set(cusId, {
                    cus_id: cusId,
                    customer_name: customerName,
                    customer_number: customerNumber,
                    sites: [siteEntry],
                });
            } else {
                const existing = map.get(cusId);
                const exists = existing.sites.some((s) => Number(s.site_id) === Number(siteEntry.site_id));
                if (!exists) existing.sites.push(siteEntry);
            }
        });
        return Array.from(map.values()).map((entry) => ({
            ...entry,
            sites: entry.sites.sort((a, b) => (a.customer_site_name || "").localeCompare(b.customer_site_name || "")),
        })).sort((a, b) => (a.customer_name || "").localeCompare(b.customer_name || ""));
    }, [scopedSiteData, t]);

    const hasMachines = selectedMachines.length > 0;
    const flattenLocations = useCallback((customersSource = []) => {
        const rows = [];
        customersSource.forEach((customer) => {
            const cusId = Number(customer?.cus_id);
            if (!Number.isFinite(cusId)) return;
            const customerName = customer.customer_name || customer.customer_site_name || `${t('UI_TEXT.CUSTOMER')} ${cusId}`;
            const customerNumber = customer.customer_number || "";
            (customer?.sites || []).forEach((site) => {
                const siteId = Number(site?.site_id);
                if (!Number.isFinite(siteId)) return;
                const siteName = site.customer_site_name || site.address_name_ln || site.site_name || `Site ${siteId}`;
                rows.push({
                    row_id: getAudienceLocationRowId(cusId, siteId),
                    site_id: siteId,
                    cus_id: cusId,
                    customer_name: customerName,
                    customer_number: customerNumber,
                    customer_site_name: siteName,
                    customer_site_number: site.customer_site_number || site.site_number || "",
                });
            });
        });
        return rows;
    }, [t]);

    const allLocationRows = useMemo(
        () => flattenLocations(aggregatedCustomerSites),
        [aggregatedCustomerSites, flattenLocations]
    );

    const machineLocationRows = useMemo(
        () => flattenLocations(audienceOptions).filter((row) => allowedSiteIdSet.has(Number(row.site_id))),
        [audienceOptions, flattenLocations, allowedSiteIdSet]
    );

    const currentLocationRows = useMemo(
        () => (hasMachines ? machineLocationRows : allLocationRows),
        [hasMachines, machineLocationRows, allLocationRows]
    );

    const locationRowsByCustomer = useMemo(() => {
        const map = new Map();
        currentLocationRows.forEach((row) => {
            const cusId = Number(row.cus_id);
            if (!map.has(cusId)) map.set(cusId, []);
            map.get(cusId).push(row);
        });
        map.forEach((rows) => rows.sort((a, b) => (a.customer_site_name || "").localeCompare(b.customer_site_name || "")));
        return map;
    }, [currentLocationRows]);

    useEffect(() => {
        if (!openDialog || visibilityScope !== "custom") return;
        if (audienceLoading && hasMachines) return;
        if (currentLocationRows.length === 0) {
            if (isEditing && hasMachines) return;
            setSelectedAudienceCustomers([]);
            setSelectedAudienceSites([]);
            return;
        }
        const validLocationIds = new Set(currentLocationRows.map((row) => row.row_id));
        const validCustomerIds = new Set(Array.from(locationRowsByCustomer.keys()).map(Number));
        setSelectedAudienceSites((prev) =>
            prev.filter((site) => validLocationIds.has(site.row_id || getAudienceLocationRowId(site.cus_id, site.site_id)))
        );
        setSelectedAudienceCustomers((prev) => prev.filter((cust) => validCustomerIds.has(Number(cust.cus_id))));
    }, [currentLocationRows, locationRowsByCustomer, openDialog, visibilityScope, audienceLoading, hasMachines, isEditing]);

    const scopedCategories = useMemo(() => {
        return (categories || []).filter((category) => {
            const id = Number(category?.id || category?.category_id);
            if (!Number.isFinite(id)) return false;
            return true;
        });
    }, [categories]);

    const categorySelectOptions = useMemo(() => {
        return (scopedCategories || []).map((category) => ({
            id: category.id,
            label: category.category_name,
        }));
    }, [scopedCategories]);

    const assignSelectedDocs = useMemo(() => {
        if (!assignModal.open) return [];
        const docMap = new Map(documents.map((doc) => [doc.id, doc]));
        return assignModal.documentIds.map((docId) => docMap.get(docId)).filter(Boolean);
    }, [assignModal, documents]);

    const documentsByCategoryId = useMemo(() => {
        const map = new Map();
        documents.forEach((doc) => {
            if (!doc || !Array.isArray(doc.categories)) return;
            doc.categories.forEach((cat) => {
                const id = Number(cat.category_id || cat.id);
                if (!Number.isFinite(id)) return;
                if (!map.has(id)) map.set(id, []);
                map.get(id).push(doc);
            });
        });
        return map;
    }, [documents]);

    const defaultCategory = useMemo(
        () => scopedCategories.find((cat) => cat.is_default) || scopedCategories.find((cat) => Number(cat?.id) === DEFAULT_CATEGORY_ID),
        [scopedCategories]
    );

    const categoryGridRows = useMemo(() => {
        return (scopedCategories || []).map((category) => {
            const id = Number(category?.id || category?.category_id);
            const scopedCount = Number.isFinite(id) ? (documentsByCategoryId.get(id)?.length || 0) : 0;
            return {
                ...category,
                document_count: scopedCount,
            };
        });
    }, [scopedCategories, documentsByCategoryId]);

    const getCategorySelectionKey = (entry) => {
        const id = entry?.id ?? entry?.category_id ?? null;
        if (id !== null && id !== undefined && String(id).trim() !== "") {
            return `id:${id}`;
        }
        const label = String(entry?.label || entry?.category_name || entry?.inputValue || entry || "").trim().toLowerCase();
        return label ? `label:${label}` : "";
    };

    const isSameCategoryOption = (option, value) => {
        const optionKey = getCategorySelectionKey(option);
        const valueKey = getCategorySelectionKey(value);
        return optionKey !== "" && optionKey === valueKey;
    };

    const mergeCategorySelections = (existingCategories = [], nextCategories = []) => {
        const seen = new Set();
        const merged = [];

        [...(existingCategories || []), ...(nextCategories || [])].forEach((entry) => {
            const key = getCategorySelectionKey(entry);
            if (!key || seen.has(key)) return;
            seen.add(key);
            merged.push(entry);
        });

        return merged;
    };

    const subtractCategorySelections = (sourceCategories = [], categoriesToRemove = []) => {
        const removeKeys = new Set(
            (categoriesToRemove || [])
                .map((entry) => getCategorySelectionKey(entry))
                .filter(Boolean)
        );

        return (sourceCategories || []).filter((entry) => {
            const key = getCategorySelectionKey(entry);
            return key && !removeKeys.has(key);
        });
    };

    const getAppliedCategoriesForSelection = (rowDetails = {}, globalCategories = []) => {
        const normalizedRowDetails = Array.isArray(rowDetails) ? { categories: rowDetails } : (rowDetails || {});
        const rowCategories = Array.isArray(normalizedRowDetails.categories) ? normalizedRowDetails.categories : [];
        const excludedCategories = Array.isArray(normalizedRowDetails.excluded_categories) ? normalizedRowDetails.excluded_categories : [];
        return subtractCategorySelections(
            mergeCategorySelections(globalCategories, rowCategories),
            excludedCategories
        );
    };

    const getAppliedRowCategories = (rowDetails = {}) => {
        return getAppliedCategoriesForSelection(rowDetails, selectedDocCategories);
    };

    const getRowNameValue = (rowDetails = {}, fallbackName = "") => {
        if (!rowDetails || typeof rowDetails !== "object") {
            return String(fallbackName || "");
        }
        if (Object.prototype.hasOwnProperty.call(rowDetails, "name")) {
            return String(rowDetails.name ?? "");
        }
        return String(fallbackName || "");
    };

    const getRowDescriptionValue = (rowDetails = {}, fallbackDescription = "") => {
        if (!rowDetails || typeof rowDetails !== "object") {
            return String(fallbackDescription || "");
        }
        if (Object.prototype.hasOwnProperty.call(rowDetails, "description")) {
            return String(rowDetails.description ?? "");
        }
        return String(fallbackDescription || "");
    };

    const syncRowCategorySelection = (nextVisibleCategories = []) => {
        const visible = normalizeCategorySelection(nextVisibleCategories);
        const globalCategories = Array.isArray(selectedDocCategories) ? selectedDocCategories : [];
        const globalKeys = new Set(globalCategories.map((entry) => getCategorySelectionKey(entry)).filter(Boolean));
        return {
            categories: visible.filter((entry) => !globalKeys.has(getCategorySelectionKey(entry))),
            excluded_categories: globalCategories.filter((entry) => !visible.some((item) => getCategorySelectionKey(item) === getCategorySelectionKey(entry))),
        };
    };

    const exclusiveDocsForDelete = useMemo(() => {
        if (!deleteCategoryModalState.category) return [];
        return documents.filter((doc) => {
            if (!doc || !Array.isArray(doc.categories)) return false;
            const categoryIds = doc.categories
                .map((cat) => Number(cat.category_id || cat.id))
                .filter((id) => Number.isFinite(id));
            return (
                categoryIds.length === 1 &&
                categoryIds[0] === deleteCategoryModalState.category.id
            );
        });
    }, [deleteCategoryModalState.category, documents]);

    const categoryDocumentsForDelete = useMemo(() => {
        if (!deleteCategoryModalState.category) return [];
        const id = Number(deleteCategoryModalState.category.id);
        if (!Number.isFinite(id)) return [];
        return documentsByCategoryId.get(id) || [];
    }, [deleteCategoryModalState.category, documentsByCategoryId]);

    const normalizeCategorySelection = (newValue) => {
        const seen = new Set();
        return (newValue || []).reduce((acc, entry) => {
            let normalized;
            if (typeof entry === "string") {
                normalized = { id: null, label: entry, isNew: true };
            } else if (entry?.inputValue) {
                normalized = { id: null, label: entry.inputValue, isNew: true };
            } else {
                const id = entry?.id ?? entry?.category_id ?? null;
                const label = entry?.label || entry?.category_name || "";
                normalized = { id, label, isNew: !id };
            }

            const key = getCategorySelectionKey(normalized);
            if (!key || seen.has(key)) return acc;
            seen.add(key);
            acc.push(normalized);
            return acc;
        }, []);
    };

    const applyCategorySelectionChangeToRowDetails = (rowDetails = {}, previousCategories = [], nextCategories = []) => {
        const normalizedRowDetails = rowDetails || {};
        const previousKeys = new Set(
            (previousCategories || [])
                .map((entry) => getCategorySelectionKey(entry))
                .filter(Boolean)
        );
        const nextKeys = new Set(
            (nextCategories || [])
                .map((entry) => getCategorySelectionKey(entry))
                .filter(Boolean)
        );
        const addedCategories = (nextCategories || []).filter((entry) => {
            const key = getCategorySelectionKey(entry);
            return key && !previousKeys.has(key);
        });
        const removedCategories = (previousCategories || []).filter((entry) => {
            const key = getCategorySelectionKey(entry);
            return key && !nextKeys.has(key);
        });

        return {
            ...normalizedRowDetails,
            categories: subtractCategorySelections(normalizedRowDetails.categories || [], removedCategories),
            excluded_categories: subtractCategorySelections(
                normalizedRowDetails.excluded_categories || [],
                [...addedCategories, ...removedCategories]
            ),
        };
    };

    const handleCategorySelection = (event, newValue) => {
        const normalized = normalizeCategorySelection(newValue);
        setSelectedDocCategories((prevSelected) => {
            setBulkFileDetails((prev) => {
                const next = { ...prev };
                selectedDocumentFiles.forEach((file) => {
                    const fileKey = getBulkFileKey(file);
                    next[fileKey] = applyCategorySelectionChangeToRowDetails(next[fileKey], prevSelected, normalized);
                });
                return next;
            });
            return normalized;
        });
        const visibleCategoryErrorKeys = (uploadRows || [])
            .map((row) => `${row?.key || getBulkFileKey(row)}_category`)
            .filter(Boolean);
        setError((prev) => {
            const next = { ...prev, categories: undefined };
            visibleCategoryErrorKeys.forEach((key) => {
                delete next[key];
            });
            return next;
        });
        setValidationErrors((prev) => {
            const next = { ...prev };
            visibleCategoryErrorKeys.forEach((key) => {
                delete next[key];
            });
            return next;
        });
    };

    const updateBulkFileDetail = (fileKey, changes) => {
        setBulkFileDetails((prev) => ({
            ...prev,
            [fileKey]: {
                ...(prev[fileKey] || {}),
                ...changes,
            },
        }));
    };

    const updateBulkRowCategorySelection = (rowKey, nextVisibleCategories = [], activeRowKeys = []) => {
        const normalized = normalizeCategorySelection(nextVisibleCategories);
        const rowKeys = (activeRowKeys || []).filter(Boolean);
        const keysToCheck = rowKeys.length > 0 ? rowKeys : [rowKey];
        const nextDetails = {
            ...bulkFileDetails,
            [rowKey]: {
                ...(bulkFileDetails[rowKey] || {}),
                ...syncRowCategorySelection(normalized),
            },
        };

        const nextGlobalCategories = (selectedDocCategories || []).filter((category) => {
            const categoryKey = getCategorySelectionKey(category);
            if (!categoryKey) return false;
            return keysToCheck.some((key) =>
                getAppliedCategoriesForSelection(nextDetails[key] || {}, selectedDocCategories)
                    .some((entry) => getCategorySelectionKey(entry) === categoryKey)
            );
        });

        if (nextGlobalCategories.length !== (selectedDocCategories || []).length) {
            const removedCategories = subtractCategorySelections(selectedDocCategories, nextGlobalCategories);
            keysToCheck.forEach((key) => {
                nextDetails[key] = {
                    ...(nextDetails[key] || {}),
                    categories: subtractCategorySelections(nextDetails[key]?.categories || [], removedCategories),
                    excluded_categories: subtractCategorySelections(nextDetails[key]?.excluded_categories || [], removedCategories),
                };
            });
            setSelectedDocCategories(nextGlobalCategories);
        }

        setBulkFileDetails(nextDetails);
    };

    const getAppliedMachinesForSelection = (rowDetails = {}, globalMachines = []) => {
        return Array.isArray(rowDetails?.machines) ? rowDetails.machines : globalMachines;
    };

    const updateBulkRowMachineSelection = (rowKey, nextMachines = [], activeRowKeys = []) => {
        const rowKeys = (activeRowKeys || []).filter(Boolean);
        const keysToCheck = rowKeys.length > 0 ? rowKeys : [rowKey];
        const normalizedMachines = Array.isArray(nextMachines) ? nextMachines : [];
        const nextDetails = {
            ...bulkFileDetails,
            [rowKey]: {
                ...(bulkFileDetails[rowKey] || {}),
                machines: normalizedMachines,
            },
        };

        const nextSelectedMachines = (selectedMachines || []).filter((machine) => {
            if (!machine?.machine_id) return false;
            return keysToCheck.some((key) =>
                getAppliedMachinesForSelection(nextDetails[key] || {}, selectedMachines)
                    .some((rowMachine) => rowMachine?.machine_id === machine.machine_id)
            );
        });

        if (nextSelectedMachines.length !== (selectedMachines || []).length) {
            setSelectedMachines(nextSelectedMachines);
        }

        setBulkFileDetails(nextDetails);
    };

    const addUrlDocumentRow = () => {
        setUrlDocumentRows((prev) => [...prev, createBlankUrlRow(prev.length)]);
    };

    const updateUrlDocumentRow = (rowKey, changes) => {
        setUrlDocumentRows((prev) =>
            prev.map((row) => (row.key === rowKey ? { ...row, ...changes } : row))
        );
    };

    const removeUrlDocumentRow = (rowKey) => {
        setUrlDocumentRows((prev) => {
            const next = prev.filter((row) => row.key !== rowKey);
            return next.length > 0 ? next : [createBlankUrlRow()];
        });
        setBulkFileDetails((prev) => {
            const next = { ...prev };
            delete next[rowKey];
            return next;
        });
    };

    const bulkMultiSelectSx = {
        "& .MuiInputBase-root": {
            minHeight: 40,
            height: 40,
            maxHeight: 40,
            alignItems: "center",
            overflow: "hidden",
            py: 0,
        },
        "& .MuiAutocomplete-inputRoot": {
            flexWrap: "nowrap",
            alignItems: "center",
            overflow: "hidden",
            minHeight: 40,
            height: 40,
            maxHeight: 40,
            py: 0,
            gap: 0.25,
        },
        "& .MuiAutocomplete-input": {
            minWidth: 40,
            flexGrow: 1,
            flexBasis: 40,
            padding: 0,
            margin: 0,
        },
        "& .MuiAutocomplete-endAdornment": {
            top: "50%",
            transform: "translateY(-50%)",
        },
        "& .MuiAutocomplete-clearIndicator": {
            marginRight: 0.25,
        },
        "& .MuiChip-root": {
            height: 26,
            maxWidth: "100%",
        },
    };

    const getMachineAutocompleteSlotProps = (rowIndex, rowCount) => {
        const shouldOpenUpward = Number(rowCount) > 2 && Number(rowIndex) >= Number(rowCount) - 2;
        return {
            popper: {
                placement: shouldOpenUpward ? "top-start" : "bottom-start",
                modifiers: [
                    { name: "flip", enabled: !shouldOpenUpward, options: { fallbackPlacements: ["top-start"] } },
                    { name: "preventOverflow", options: { rootBoundary: "viewport", padding: 12 } },
                ],
            },
        };
    };

    const renderScrollableTags = (value = [], getTagProps = () => ({}), getLabel = (option) => {
        if (typeof option === "string") return option;
        return String(option?.label || option?.category_name || option?.machine_number || option?.machine_name || "");
    }, endControlReserve = 76) => (
        <Box
            sx={{
                display: "flex",
                flexWrap: "nowrap",
                gap: 0.5,
                height: 32,
                overflowX: "auto",
                overflowY: "hidden",
                width: "fit-content",
                maxWidth: `calc(100% - ${endControlReserve}px)`,
                minWidth: 0,
                flex: "0 1 auto",
                alignItems: "center",
                pr: 0.5,
                "&::-webkit-scrollbar": {
                    height: 6,
                },
            }}
        >
            {value.map((option, index) => (
                <Chip
                    {...getTagProps({ index })}
                    key={`${getLabel(option)}-${index}`}
                    size="small"
                    label={getLabel(option)}
                    sx={{ maxWidth: "100%", flex: "0 0 auto" }}
                />
            ))}
        </Box>
    );

    const urlRowFieldSx = {
        position: "relative",
        "& .MuiFormHelperText-root": {
            position: "absolute",
            top: "100%",
            left: 0,
            mt: 0.5,
            lineHeight: "20px",
            whiteSpace: "nowrap",
        },
    };

    const mergeMachineSelections = (existingMachines = [], incomingMachines = []) => {
        const machineMap = new Map();
        [...(Array.isArray(existingMachines) ? existingMachines : []), ...(Array.isArray(incomingMachines) ? incomingMachines : [])]
            .filter(Boolean)
            .forEach((machine) => {
                if (machine?.machine_id) {
                    machineMap.set(machine.machine_id, machine);
                }
            });
        return Array.from(machineMap.values());
    };

    const removeMachinesFromSelection = (existingMachines = [], removedMachines = []) => {
        const removedIds = new Set((Array.isArray(removedMachines) ? removedMachines : []).map((machine) => machine?.machine_id).filter(Boolean));
        return (Array.isArray(existingMachines) ? existingMachines : []).filter((machine) => !removedIds.has(machine?.machine_id));
    };

    const removeSelectedDocumentFile = (fileToRemove) => {
        const fileKey = getBulkFileKey(fileToRemove);
        setNewDoc((prev) => ({
            ...prev,
            files: (prev.files || []).filter((file) => getBulkFileKey(file) !== fileKey),
        }));
        setBulkFileDetails((prev) => {
            const next = { ...prev };
            delete next[fileKey];
            return next;
        });
    };

    const handleOpenAddCategoryModal = () => {
        if (!canCreateDocuments) return;
        setAddCategoryModalOpen(true);
    };

    const handleOpenEditCategoryModal = (category) => {
        if (!category || Number(category?.id) === DEFAULT_CATEGORY_ID) return;
        if (!canEditDocuments) return;
        setEditCategoryModal({ open: true, category });
    };

    const handleCreateCategory = async (name) => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            throw new Error(t('UI_TEXT.CATEGORY_REQUIRED'));
        }
        try {
            await createDocumentCategory({ category_name: trimmedName });
            Swal.fire({
                icon: 'success',
                title: t('ALERT_MESSAGES.SUCCESS'),
                text: t('ALERT_MESSAGES.CATEGORY_CREATED') || 'Category created successfully',
            });
            fetchCategories();
        } catch (error) {
            const message = error?.response?.data?.message || t('ALERT_MESSAGES.ERROR');
            throw new Error(message);
        }
    };

    const handleEditCategorySubmit = async (name) => {
        if (!editCategoryModal.category) {
            throw new Error(t('ALERT_MESSAGES.ERROR'));
        }
        const categoryId = Number(editCategoryModal.category.id);
        const trimmedName = name.trim();
        if (!trimmedName) {
            throw new Error(t('UI_TEXT.CATEGORY_REQUIRED'));
        }
        try {
            await updateDocumentCategory(categoryId, { category_name: trimmedName });
            setCategories((prev) =>
                prev.map((category) =>
                    Number(category?.id) === categoryId
                        ? { ...category, category_name: trimmedName }
                        : category
                )
            );
            setDocuments((prev) =>
                prev.map((doc) => ({
                    ...doc,
                    categories: Array.isArray(doc?.categories)
                        ? doc.categories.map((category) =>
                            Number(category?.category_id || category?.id) === categoryId
                                ? { ...category, category_name: trimmedName }
                                : category
                        )
                        : doc?.categories,
                }))
            );
            setCategoryDetailsModal((prev) => {
                if (!prev.open || Number(prev.category?.id) !== categoryId) return prev;
                return {
                    ...prev,
                    category: { ...prev.category, category_name: trimmedName },
                    documents: (prev.documents || []).map((doc) => ({
                        ...doc,
                        categories: Array.isArray(doc?.categories)
                            ? doc.categories.map((category) =>
                                Number(category?.category_id || category?.id) === categoryId
                                    ? { ...category, category_name: trimmedName }
                                    : category
                            )
                            : doc?.categories,
                    })),
                };
            });
            Swal.fire({
                icon: 'success',
                title: t('ALERT_MESSAGES.SUCCESS'),
                text: t('ALERT_MESSAGES.CATEGORY_UPDATED') || 'Category updated successfully',
            });
            fetchCategories();
            fetchDocuments();
        } catch (error) {
            const message = error?.response?.data?.message || t('ALERT_MESSAGES.ERROR');
            throw new Error(message);
        }
    };

    const handleDeleteCategoryRequest = (category) => {
        if (!category) return;
        if (Number(category.id) === DEFAULT_CATEGORY_ID || category?.is_default) {
            Swal.fire({
                icon: "info",
                title: t('ALERT_MESSAGES.WARNING_TITLE') || t('ALERT_MESSAGES.WARNING'),
                text: t('UI_TEXT.DEFAULT_CATEGORY_LOCKED'),
            });
            return;
        }
        if (!canDeleteDocuments) return;
        setDeleteCategoryModalState({ open: true, category });
    };

    const handleOpenAssignModal = (category) => {
        if (!canAssignDocuments) return;
        setAssignModal({
            open: true,
            category,
            documentIds: Array.isArray(category?.documents) ? category.documents.map((doc) => doc.document_id) : [],
        });
    };

    const handleAssignModalClose = () => {
        setAssignModal({ open: false, category: null, documentIds: [] });
    };

    const handleAssignDocumentsChange = (event, value) => {
        const ids = (value || []).map((doc) => doc.id || doc.document_id).filter(Boolean);
        setAssignModal((prev) => ({ ...prev, documentIds: ids }));
    };

    const handleAssignDocumentsSave = async () => {
        if (!assignModal.category) return;
        try {
            await updateCategoryDocuments(assignModal.category.id, { document_ids: assignModal.documentIds });
            Swal.fire({ icon: 'success', title: t('ALERT_MESSAGES.SUCCESS'), text: t('ALERT_MESSAGES.CATEGORY_UPDATED') || 'Category updated successfully' });
            handleAssignModalClose();
            fetchCategories();
            fetchDocuments();
        } catch (error) {
            const message = error?.response?.data?.message || t('ALERT_MESSAGES.ERROR');
            Swal.fire({ icon: 'error', title: t('ALERT_MESSAGES.ERROR'), text: message });
        }
    };

    const handleRemoveDocumentFromCategory = async (categoryId, documentId) => {
        if (!categoryId || !documentId) return;
        const confirmation = await Swal.fire({
            title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
            text: t('UI_TEXT.REMOVE_FROM_CATEGORY_CONFIRM'),
            icon: 'warning',
            showCancelButton: true,
        });
        if (!confirmation.isConfirmed) return;
        try {
            const docsForCategory = documentsByCategoryId.get(categoryId) || [];
            const remainingIds = docsForCategory
                .map((doc) => doc.id)
                .filter((id) => Number.isFinite(id) && id !== documentId);
            await updateCategoryDocuments(categoryId, { document_ids: remainingIds });
            Swal.fire({ icon: 'success', title: t('ALERT_MESSAGES.SUCCESS'), text: t('ALERT_MESSAGES.CATEGORY_UPDATED') });
            fetchCategories();
            fetchDocuments();
        } catch (error) {
            const message = error?.response?.data?.message || t('ALERT_MESSAGES.ERROR');
            Swal.fire({ icon: 'error', title: t('ALERT_MESSAGES.ERROR'), text: message });
        }
    };

    const handleConfirmDeleteCategory = async ({ reassignCategoryId }) => {
        if (!deleteCategoryModalState.category) return;
        try {
            await deleteDocumentCategory(deleteCategoryModalState.category.id, {
                reassign_category_id: reassignCategoryId,
                fallback_to_uncategorized: !reassignCategoryId,
            });
            Swal.fire({
                icon: 'success',
                title: t('ALERT_MESSAGES.DELETED'),
                text: t('ALERT_MESSAGES.CATEGORY_DELETED') || 'Category deleted successfully',
            });
            const deletedCategoryId = deleteCategoryModalState.category.id;
            setDeleteCategoryModalState({ open: false, category: null });
            if (categoryDetailsModal.category?.id === deletedCategoryId) {
                handleCloseCategoryDetails();
            }
            fetchCategories();
            fetchDocuments();
        } catch (error) {
            const message = error?.response?.data?.message || t('ALERT_MESSAGES.ERROR');
            throw new Error(message);
        }
    };

    const handleOpenCategoryDetails = (category) => {
        if (!category) return;
        const docsForCategory = documentsByCategoryId.get(category.id) || [];
        setCategoryDetailsModal({ open: true, category, documents: docsForCategory });
    };

    const handleCloseCategoryDetails = () => {
        setCategoryDetailsModal({ open: false, category: null, documents: [] });
    };

    const handleCloseDeleteCategoryModal = () => {
        setDeleteCategoryModalState({ open: false, category: null });
    };

    const handleViewDocument = (doc) => {
        if (!doc) return;
        setViewDoc(doc);
        setViewModalOpen(true);
    };

    const handleCloseViewDocument = () => {
        setViewDoc(null);
        setViewModalOpen(false);
    };

    useEffect(() => {
        if (!categoryDetailsModal.open || !categoryDetailsModal.category) return;
        const docsForCategory = documentsByCategoryId.get(categoryDetailsModal.category.id) || [];
        setCategoryDetailsModal((prev) => {
            if (!prev.open || prev.category?.id !== categoryDetailsModal.category.id) {
                return prev;
            }
            const prevDocs = prev.documents || [];
            const sameLength = prevDocs.length === docsForCategory.length;
            const sameItems =
                sameLength &&
                prevDocs.every((doc, idx) => doc?.id === docsForCategory[idx]?.id);
            if (sameItems) return prev;
            return { ...prev, documents: docsForCategory };
        });
    }, [categoryDetailsModal.open, categoryDetailsModal.category, documentsByCategoryId]);

    const handleAddDocumentFromCategory = (category) => {
        if (!category || !canCreateDocuments) return;
        if (categoryDetailsModal.open) {
            handleCloseCategoryDetails();
        }
        resetFormState();
        setSelectedDocCategories([{ id: category.id, label: category.category_name, isNew: false }]);
        setIsEditing(false);
        setEditDocId(null);
        setOpenDialog(true);
        setTabIndex(0);
    };

    const resetFormState = () => {
        setNewDoc(INITIAL_DOCUMENT);
        setSelectedDocCategories([]);
        setSelectedMachines([]);
        setSelectedAudienceCustomers([]);
        setSelectedAudienceSites([]);
        setAudienceOptions([]);
        setVisibilityScope("all");
        setUploadTabIndex(0);
        setError({});
        setFormErrors([]);
        setValidationErrors({});
        setMachineSearch("");
        setFormSiteNumber(selectedSite || "");
        setFileDropzoneKey((k) => k + 1);
        setBulkFileDetails({});
        setUrlDocumentRows([createBlankUrlRow()]);
        setUploadNotificationsEnabled(false);
        setUploadProgressItems([]);
        setUploadPhase("idle");
    };

    const restoreFailedBatchContext = (context) => { 
        if (!context) return;
        resetFormState();
        setIsEditing(false);
        setEditDocId(null);
        setOpenDialog(true);
        setUploadTabIndex(0);
        setNewDoc((prev) => ({
            ...prev,
            ...context.newDoc,
            attachment_type: context.attachment_type || prev.attachment_type,
            files: Array.isArray(context.files) ? context.files : [],
            url: context.url || "",
        }));
        setSelectedDocCategories(Array.isArray(context.selectedDocCategories) ? context.selectedDocCategories : []);
        setSelectedMachines(Array.isArray(context.selectedMachines) ? context.selectedMachines : []);
        setVisibilityScope(context.visibilityScope || "all");
        setFormSiteNumber(context.formSiteNumber || selectedSite || "");
        setBulkFileDetails(context.bulkFileDetails || {});
        setUrlDocumentRows(Array.isArray(context.urlDocumentRows) && context.urlDocumentRows.length > 0 ? context.urlDocumentRows : [createBlankUrlRow()]);
        setSelectedAudienceCustomers(Array.isArray(context.selectedAudienceCustomers) ? context.selectedAudienceCustomers : []);
        setSelectedAudienceSites(Array.isArray(context.selectedAudienceSites) ? context.selectedAudienceSites : []);
        setUploadNotificationsEnabled(Boolean(context.uploadNotificationsEnabled));
        setError({});
        setFormErrors([]);
        setValidationErrors({});
        setUploadProgressItems([]);
        setUploadPhase("idle");
    };

    const captureFailedBatchContext = (results = [], source = {}) => {
        const failedIndexes = Array.isArray(results)
            ? results
                .map((item, index) => (getUploadResultStatus(item?.status) === "failed" ? index : -1))
                .filter((index) => index >= 0)
            : [];
        const failedFiles = Array.isArray(source.selectedDocumentFiles)
            ? failedIndexes.map((index) => source.selectedDocumentFiles[index]).filter(Boolean)
            : [];
        const failedUrlRows = Array.isArray(source.urlDocumentRows)
            ? failedIndexes.map((index) => source.urlDocumentRows[index]).filter(Boolean)
            : [];
        const failedFileDetails = {};
        failedFiles.forEach((file) => {
            const fileKey = getBulkFileKey(file);
            if (source.bulkFileDetails?.[fileKey]) {
                failedFileDetails[fileKey] = source.bulkFileDetails[fileKey];
            }
        });
        const failedUrlDetails = {};
        failedUrlRows.forEach((row) => {
            const rowKey = row?.key;
            if (rowKey && source.bulkFileDetails?.[rowKey]) {
                failedUrlDetails[rowKey] = source.bulkFileDetails[rowKey];
            }
        });
        return {
            attachment_type: source.attachment_type || "file",
            files: failedFiles,
            urlDocumentRows: failedUrlRows,
            bulkFileDetails: {
                ...failedFileDetails,
                ...failedUrlDetails,
            },
            selectedDocCategories: source.selectedDocCategories || [],
            selectedMachines: source.selectedMachines || [],
            visibilityScope: source.visibilityScope || "all",
            selectedAudienceCustomers: source.selectedAudienceCustomers || [],
            selectedAudienceSites: source.selectedAudienceSites || [],
            formSiteNumber: source.formSiteNumber || "",
            newDoc: {
                name: source.newDoc?.name || "",
                description: source.newDoc?.description || "",
                status: source.newDoc?.status || "private",
                url: source.newDoc?.url || "",
            },
            uploadNotificationsEnabled: Boolean(source.uploadNotificationsEnabled),
        };
    };

    const updateErrorsForKeys = (keys, newErrors) => {
        setError((prev) => {
            const next = { ...prev };
            keys.forEach((key) => {
                delete next[key];
            });
            if (Object.keys(newErrors).length === 0) return next;
            return { ...next, ...newErrors };
        });
    };

    const validateNextPage = () => {
        const errors = {};
        const fileNames = [];
        const rowsToValidate = isEditing
            ? uploadRows
            : !isEditing && newDoc.attachment_type === "url"
                ? urlDocumentRows
                : selectedDocumentFiles;
        
        // Validate each file's name and machine mapping
        rowsToValidate.forEach((file) => {
            const fileKey = file?.key || getBulkFileKey(file);
            const fileDetails = bulkFileDetails[fileKey] || {};
            const fileMachines = fileDetails.machines || selectedMachines;
            const fileMachineCount = Array.isArray(fileMachines) ? fileMachines.length : 0;
            const fileName = newDoc.attachment_type === "url"
                ? getRowNameValue(fileDetails, file?.name || "")
                : getRowNameValue(fileDetails, getFileBaseName(file?.name || file?.url || ""));
            const fileUrl = newDoc.attachment_type === "url"
                ? String(file?.url || fileDetails.url || "").trim()
                : "";
            
            if (fileMachineCount < 1) {
                errors[`${fileKey}_machine`] = "Select at least one machine";
            }
            
            // Validate name for each file
            if (!fileName || fileName.trim() === "") {
                errors[fileKey] = "Name is required";
            }
            if (!isEditing && newDoc.attachment_type === "url") {
                const rawUrl = fileUrl;
                const isValidUrl = (() => { try { const url = new URL(rawUrl); return url.protocol === 'http:' || url.protocol === 'https:'; } catch { return false; } })();
                if (!rawUrl) {
                    errors[`${fileKey}_url`] = "URL is required";
                } else if (!isValidUrl) {
                    errors[`${fileKey}_url`] = "Enter a valid http(s) URL";
                }
            }
            
            // Check for duplicate names
            if (fileName && fileName.trim() !== "") {
                const normalizedName = fileName.trim();
                if (fileNames.includes(normalizedName)) {
                    errors[fileKey] = "Name already exists";
                } else {
                    fileNames.push(normalizedName);
                }
            }
        });
        
        setValidationErrors(errors);
        if (Object.keys(errors).length > 0) {
            logDocumentUploadEvent("step_two_validation_failed", {
                error_keys: Object.keys(errors),
                error_preview: errors,
            });
        }
        
        // Return true if no errors, false otherwise
        return Object.keys(errors).length === 0;
    };

    const validateStepOne = () => {
        const newErrors = {};
        const cellErrors = {};
        if (!isEditing && newDoc.attachment_type === 'file' && (!newDoc.files || newDoc.files.length === 0)) newErrors["file"] = t('UI_TEXT.FILE_REQUIRED');
        if (!isEditing && newDoc.attachment_type === 'file' && selectedDocumentFiles.length > 0) {
            const existingNameMap = new Map(
                (Array.isArray(documents) ? documents : [])
                    .map((doc) => String(doc?.name || doc?.document_name || "").trim())
                    .filter(Boolean)
                    .map((name) => [normalizeDocumentNameKey(name), name])
            );
            const batchNameMap = new Map();
            selectedDocumentFiles.forEach((file) => {
                const fileKey = getBulkFileKey(file);
                const fileDetails = bulkFileDetails[fileKey] || {};
                const resolvedName = getRowNameValue(fileDetails, getFileBaseName(file?.name || ""));
                const normalizedName = normalizeDocumentNameKey(resolvedName);
                if (!normalizedName) {
                    cellErrors[fileKey] = "Name is required";
                } else if (existingNameMap.has(normalizedName) || batchNameMap.has(normalizedName)) {
                    cellErrors[fileKey] = "Name already exists";
                } else {
                    batchNameMap.set(normalizedName, resolvedName);
                }

            });
            setValidationErrors((prev) => {
                const next = { ...prev };
                selectedDocumentFiles.forEach((file) => {
                    const fileKey = getBulkFileKey(file);
                    delete next[fileKey];
                    delete next[`${fileKey}_category`];
                });
                return { ...next, ...cellErrors };
            });
        }
        if (newDoc.attachment_type === 'url' && !isEditing) {
            const isValidUrl = (u) => { try { const url = new URL(u); return url.protocol === 'http:' || url.protocol === 'https:'; } catch { return false; } };
            const existingNameMap = new Map(
                (Array.isArray(documents) ? documents : [])
                    .map((doc) => String(doc?.name || doc?.document_name || "").trim())
                    .filter(Boolean)
                    .map((name) => [normalizeDocumentNameKey(name), name])
            );
            const batchNameMap = new Map();
            (urlDocumentRows || []).forEach((row) => {
                const rowKey = row?.key || "url-row";
                const rowDetails = bulkFileDetails[rowKey] || {};
                const rowName = getRowNameValue(rowDetails, row?.name || "");
                const rowUrl = String(row?.url || "").trim();
                if (!rowName) cellErrors[`${rowKey}_name`] = "Name is required";
                else {
                    const normalizedName = normalizeDocumentNameKey(rowName);
                    if (existingNameMap.has(normalizedName) || batchNameMap.has(normalizedName)) {
                        cellErrors[`${rowKey}_name`] = "Name already exists";
                    } else {
                        batchNameMap.set(normalizedName, rowName);
                    }
                }
                if (!rowUrl) cellErrors[`${rowKey}_url`] = "URL is required";
                else if (!isValidUrl(rowUrl)) cellErrors[`${rowKey}_url`] = "Enter a valid http(s) URL";
            });
            setValidationErrors((prev) => {
                const next = { ...prev };
                (urlDocumentRows || []).forEach((row) => {
                    const rowKey = row?.key || "url-row";
                    delete next[`${rowKey}_name`];
                    delete next[`${rowKey}_url`];
                    delete next[`${rowKey}_category`];
                });
                return { ...next, ...cellErrors };
            });
        }
        if (isEditing) {
            const editName = String(newDoc.name ?? editingDocument?.name ?? "").trim();
            if (!editName) newErrors.name = "Name is required";
            if (!selectedDocCategories || selectedDocCategories.length === 0) {
                newErrors.categories = "Category is required";
            }
        }
        updateErrorsForKeys(["name", "categories", "file", "url", "site"], newErrors);
        if (Object.keys(newErrors).length > 0 || Object.keys(cellErrors).length > 0) {
            logDocumentUploadEvent("step_one_validation_failed", {
                error_keys: [...Object.keys(newErrors), ...Object.keys(cellErrors)],
                error_preview: { ...newErrors, ...cellErrors },
            });
            setFormErrors(Object.values(newErrors));
            return false;
        }
        setFormErrors([]);
        return true;
    };

    const buildVisibilityPayload = () => {
        if (visibilityScope !== "custom") return [];
        const customerEntries = selectedAudienceCustomers.map((cust) => ({
            cus_id: cust.cus_id,
            all_sites: true,
        }));
        const blockedCustomers = new Set(selectedAudienceCustomers.map((cust) => cust.cus_id));
        const seenLocations = new Set();
        const siteEntries = selectedAudienceSites
            .filter((site) => site?.site_id && !blockedCustomers.has(site.cus_id))
            .filter((site) => {
                const locationId = site.row_id || getAudienceLocationRowId(site.cus_id, site.site_id);
                if (seenLocations.has(locationId)) return false;
                seenLocations.add(locationId);
                return true;
            })
            .map((site) => ({
                cus_id: site.cus_id,
                site_id: site.site_id,
                all_sites: false,
            }));
        return [...customerEntries, ...siteEntries];
    };

    const buildFileUploadMetadata = (file, resolvedSiteId) => {
        const fileKey = getBulkFileKey(file);
        const fileDetails = bulkFileDetails[fileKey] || {};
        const fileCategories = getAppliedRowCategories(fileDetails);
        const fileMachines = fileDetails.machines || selectedMachines;
        const fileVisibilityScope = fileDetails.visibilityScope || visibilityScope;
        const fileStatus = fileDetails.status || newDoc.status || "private";

        return {
            name: getRowNameValue(fileDetails, getFileBaseName(file?.name || "")),
            description: getRowDescriptionValue(fileDetails, newDoc.description ?? ""),
            site_id: resolvedSiteId,
            attachment_type: newDoc.attachment_type,
            assigned_machines: fileMachines.map((machine) => machine.machine_id),
            visibility_scope: fileVisibilityScope,
            visibility_targets: fileVisibilityScope === "custom" ? buildVisibilityPayload() : [],
            category_ids: fileCategories.filter((cat) => cat?.id).map((cat) => cat.id),
            new_categories: fileCategories.filter((cat) => !cat?.id).map((cat) => cat.label),
            visibility_status: fileStatus,
        };
    };

    const buildUrlUploadMetadata = (row, resolvedSiteId) => {
        const rowKey = row?.key || `url-${Date.now()}`;
        const rowDetails = bulkFileDetails[rowKey] || {};
        const rowCategories = getAppliedRowCategories(rowDetails);
        const rowMachines = rowDetails.machines || selectedMachines;
        const rowVisibilityScope = rowDetails.visibilityScope || visibilityScope;
        const rowStatus = rowDetails.status || newDoc.status || "private";
        return {
            name: getRowNameValue(rowDetails, row?.name || ""),
            description: getRowDescriptionValue(rowDetails, row?.description ?? newDoc.description ?? ""),
            site_id: resolvedSiteId,
            attachment_type: "url",
            attachment: String(rowDetails.url || row?.url || "").trim(),
            assigned_machines: rowMachines.map((machine) => machine.machine_id),
            visibility_scope: rowVisibilityScope,
            visibility_targets: rowVisibilityScope === "custom" ? buildVisibilityPayload() : [],
            category_ids: rowCategories.filter((cat) => cat?.id).map((cat) => cat.id),
            new_categories: rowCategories.filter((cat) => !cat?.id).map((cat) => cat.label),
            visibility_status: rowStatus,
        };
    };

    const uploadFileToPresignedUrl = (uploadUrl, file, contentType, onProgress) =>
        new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("PUT", uploadUrl, true);
            xhr.timeout = 0;
            if (contentType) {
                xhr.setRequestHeader("Content-Type", contentType);
            }
            if (xhr.upload && typeof onProgress === "function") {
                xhr.upload.onprogress = (event) => {
                    if (!event || !event.lengthComputable) return;
                    const percent = Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)));
                    onProgress(percent);
                };
            }
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve(true);
                    return;
                }
                reject(new Error(`Upload failed with status ${xhr.status}`));
            };
            xhr.onerror = () => reject(new Error("Network error while uploading file"));
            xhr.onabort = () => reject(new Error("File upload was aborted"));
            xhr.send(file);
        });

    const formatDocumentDate = (value) => {
        if (!value) return "-";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "-";
        return date.toLocaleDateString();
    };

    const CATEGORY_VISIBLE_LIMIT = 4;
    const columns = [
        { field: "name", headerName: t('TABLE_HEADERS.NAME'), width: 300 },
        {
            field: "categories",
            headerName: t('LABELS.CATEGORY'),
            width: 300,
            getSearchValue: (params) => {
                const cats = Array.isArray(params.row?.categories) ? params.row.categories : [];
                return cats.map((cat) => cat?.category_name || "").filter(Boolean).join(" ");
            },
            renderCell: (params) => {
                const cats = Array.isArray(params.row?.categories) ? params.row.categories : [];
                return (
                    <CategoryChipsCell
                        cats={cats}
                        rowId={params.id}
                        compactLimit={CATEGORY_VISIBLE_LIMIT}
                        noCategoryLabel={t('UI_TEXT.UNCATEGORIZED') || 'Uncategorized'}
                        colWidth={params?.colDef?.computedWidth || params?.colDef?.width || 300}
                    />
                );
            }
        },
        {
            field: "description",
            headerName: t('TABLE_HEADERS.DESCRIPTION'),
            flex: 2,
            renderCell: (params) => {
                // Remove HTML tags and trim whitespace
                const plainText = (params.value || '')
                    .replace(/<[^>]+>/g, '') // strip HTML tags
                    .trim();

                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center', // vertically center
                            height: '100%',
                            width: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                lineHeight: '1.3em',
                                maxHeight: '2.6em',
                                width: '100%',
                                color:
                                    plainText === t('UI_TEXT.NO_DESCRIPTION_AVAILABLE_1')
                                        ? 'text.secondary'
                                        : 'inherit',
                                wordBreak: 'break-word',
                            }}
                        >
                            {plainText || ''}
                        </Box>
                    </Box>
                );
            },
            cellClassName: (params) => {
                const plainText = (params.value || '').replace(/<[^>]+>/g, '').trim();
                return plainText === t('UI_TEXT.NO_DESCRIPTION_AVAILABLE_1') ? "gray-text" : "";
            },
        },
        {
            field: "visibility_status",
            headerName: t('TABLE_HEADERS.STATUS') || 'Status',
            width: 140,
            renderCell: (params) => {
                const value = params.value === 'public' ? 'public' : 'private';
                const label = value === 'public' ? (t('LABELS.PUBLIC') || 'Public') : (t('LABELS.PRIVATE') || 'Private');
                return (
                    <Chip
                        size="small"
                        label={label}
                        color={value === 'public' ? 'success' : 'default'}
                        variant="outlined"
                    />
                );
            },
        },
        {
            field: "assigned_machines",
            headerName: t('TABLE_HEADERS.ASSIGNED_MACHINE'),
            flex: 1.5,
            valueGetter: (_value, row) => {
                const assignedMachines = row?.assigned_machines;
                if (Array.isArray(assignedMachines) && assignedMachines.length > 0) {
                    const names = assignedMachines
                        .map((m) => m?.machine_name || formatMachineNumberFromRow(m) || m?.machine_id)
                        .filter(Boolean)
                        .join(", ");
                    return names || t('UI_TEXT.NO_MACHINES_ASSIGNED_1');
                }
                return t('UI_TEXT.NO_MACHINES_ASSIGNED_1');
            },
            cellClassName: (params) => params.value === t('UI_TEXT.NO_MACHINES_ASSIGNED_1') ? "gray-text" : "",
        },
        {
            field: "machine_number",
            headerName: t('TABLE_HEADERS.MACHINE_NUMBER') || "Machine Number",
            flex: 1.5,
            valueGetter: (_value, row) => {
                const assignedMachines = row?.assigned_machines;
                if (Array.isArray(assignedMachines) && assignedMachines.length > 0) {
                    const numbers = assignedMachines
                        .map((m) => formatMachineNumberFromRow(m))
                        .filter(Boolean)
                        .join(", ");
                    return numbers || t('UI_TEXT.NO_MACHINES_ASSIGNED_1');
                }
                return t('UI_TEXT.NO_MACHINES_ASSIGNED_1');
            },
            cellClassName: (params) => params.value === t('UI_TEXT.NO_MACHINES_ASSIGNED_1') ? "gray-text" : "",
        },
        {
            field: "version_number",
            headerName: t('TABLE_HEADERS.VERSION_NUMBER') || "Version Number",
            width: 150,
            valueGetter: (value, row) => row?.version_number ?? value ?? "1",
        },
        {
            field: "created_date",
            headerName: t('TABLE_HEADERS.CREATED_DATE') || "Created Date",
            width: 150,
            valueGetter: (value, row) => formatDocumentDate(
                row?.created_date || row?.created_at || row?.createdAt || value
            ),
        },
        {
            field: "modified_date",
            headerName: t('TABLE_HEADERS.MODIFIED_DATE') || "Modified Date",
            width: 150,
            valueGetter: (value, row) => formatDocumentDate(
                row?.modified_date || row?.updated_at || row?.modified_at || row?.updatedAt || value
            ),
        },
        {
            field: "actions",
            headerName: t('TABLE_HEADERS.ACTIONS'),
            width: 100,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => {
                if (!canEditDocuments && !canDeleteDocuments) return null;
                return (
                    <Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <Box sx={{ display: "flex", justifyContent: 'center', alignItems: 'center' }}>
                            {canEditDocuments && (
                                <IconButton color="primary" onClick={(event) => { event.stopPropagation(); handleEditOpen(params.row); }}>
                                    <EditIcon />
                                </IconButton>
                            )}
                            {canDeleteDocuments && (
                                <IconButton color="error" onClick={(event) => { event.stopPropagation(); handleDeleteDocument(params.row.id); }}>
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                );
            },
        },
    ];

    const categoryColumns = [
        { field: "category_name", headerName: t('LABELS.CATEGORY'), flex: 1.8, minWidth: 260 },
        { field: "document_count", headerName: t('TABLE_HEADERS.DOCUMENTS') || 'Documents #', width: 160, minWidth: 140 },
        {
            field: "actions",
            headerName: t('TABLE_HEADERS.ACTIONS'),
            width: 360,
            minWidth: 360,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const isDefault = Number(params.row?.id) === DEFAULT_CATEGORY_ID || params.row?.is_default;
                return (
                    <Box sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'center', width: '100%', py: 0.5 }}>
                        <Box sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'center', columnGap: 1.25, rowGap: 0.75, flexWrap: 'wrap', width: '100%' }}>
                            {canAssignDocuments && (
                                <Button size="small" variant="outlined" onClick={(event) => { event.preventDefault(); event.stopPropagation(); handleOpenAssignModal(params.row); }} >
                                    {t('BUTTON.ASSIGN') || 'Assign'}
                                </Button>
                            )}
                            {canCreateDocuments && (
                                <Button size="small" variant="outlined" onClick={(event) => { event.preventDefault(); event.stopPropagation(); handleAddDocumentFromCategory(params.row); }}>
                                    {t('BUTTON.ADD_DOCUMENT') || 'Add Doc'}
                                </Button>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', ml: { xs: 0, md: 'auto' } }}>
                                {canEditDocuments && !isDefault && (
                                    <IconButton color="primary" onClick={(event) => { event.preventDefault(); event.stopPropagation(); handleOpenEditCategoryModal(params.row); }}>
                                        <EditIcon />
                                    </IconButton>
                                )}
                                {canDeleteDocuments && !isDefault && (
                                    <IconButton color="error" onClick={(event) => { event.preventDefault(); event.stopPropagation(); handleDeleteCategoryRequest(params.row); }}>
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    </Box>
                );
            },
        },
    ];

    const [selectedRows, setSelectedRows] = useState([]);

    const customButtonsCategory = [
        canCreateDocuments
            ? {
                label: t('LABELS.ADD_CATEGORY'),
                icon: <AddCircle />,
                onClick: handleOpenAddCategoryModal,
            }
            : null,
    ].filter(Boolean);

    const logDocumentUploadEvent = (stage, details = {}) => {
        console.info("[documents][ui][upload]", stage, details);
    };

    const handleOpenAddDocumentModal = () => {
        logDocumentUploadEvent("add_document_button_clicked", {
            isEditing,
            selected_site: formSiteNumber || selectedSite || null,
        });
        resetFormState();
        setIsEditing(false);
        setEditDocId(null);
        setUploadTabIndex(0);
        setOpenDialog(true);
        logDocumentUploadEvent("add_document_modal_opened", { upload_tab: 0 });
    };

    const customButtons = [
        canCreateDocuments
            ? {
                label: t('LABELS.ADD_DOCUMENTS'),
                icon: <AddCircle />,
                onClick: handleOpenAddDocumentModal,
            }
            : null,
        canCreateDocuments
            ? {
                label: t('LABELS.ADD_CATEGORY'),
                icon: <AddCircle />,
                onClick: handleOpenAddCategoryModal,
            }
            : null,
        canDeleteDocuments
            ? {
                label: 'Delete Selected',
                icon: <DeleteIcon />,
                color: "error",
                disabled: !selectedRows || selectedRows.length === 0,
                onClick: async () => {
                    if (!selectedRows || selectedRows.length === 0) return;
                    const confirmation = await Swal.fire({
                        title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
                        text: 'Selected documents will be permanently deleted',
                        icon: 'warning',
                        showCancelButton: true,
                    });
                    if (!confirmation.isConfirmed) return;
                    try {
                        await Promise.all(selectedRows.map((id) => deleteDocument(id)));
                        setDocuments((prev) => prev.filter((d) => !selectedRows.includes(d.id)));
                        setSelectedRows([]);
                        Swal.fire({ icon: 'success', title: t('ALERT_MESSAGES.DELETED'), text: t('ALERT_MESSAGES.DOCUMENT_DELETED_SUCCESSFULLY') });
                    } catch (e) {
                        Swal.fire({ icon: 'error', title: t('ALERT_MESSAGES.ERROR'), text: t('ALERT_MESSAGES.COULD_NOT_DELETE_DOCUMENT') });
                    }
                },
            }
            : null,
    ].filter(Boolean);

    const fetchDocuments = useCallback(async () => {
        if (!selectedSiteId) {
            setDocuments([]);
            setDocumentsLoading(false);
            return;
        }
        setDocumentsLoading(true);
        try {
            const docRes = await getDocumentsMetaData(selectedSiteId);
            const docs = Array.isArray(docRes?.data) ? docRes.data : [];
            const siteMachineById = new Map(
                currentSiteMachines
                    .filter((machine) => machine && machine.machine_id)
                    .map((machine) => [String(machine.machine_id), machine])
            );
            setDocuments(
                docs.map((doc) => ({
                    ...doc,
                    assigned_machines: Array.isArray(doc?.machine_details) && doc.machine_details.length > 0
                        ? doc.machine_details
                        : Array.isArray(doc?.assigned_machines)
                            ? doc.assigned_machines
                                .map((machineId) => siteMachineById.get(String(machineId)))
                                .filter(Boolean)
                            : [],
                    machine_details: Array.isArray(doc?.machine_details) && doc.machine_details.length > 0
                        ? doc.machine_details
                        : Array.isArray(doc?.assigned_machines)
                            ? doc.assigned_machines
                                .map((machineId) => siteMachineById.get(String(machineId)))
                                .filter(Boolean)
                            : [],
                }))
            );
        } catch (err) {
            console.error("Error fetching data", err);
            setDocuments([]);
        } finally {
            setDocumentsLoading(false);
        }
    }, [currentSiteMachines, selectedSiteId]);

    const fetchCategories = useCallback(async () => {
        setCategoriesLoading(true);
        try {
            const catRes = await getDocumentCategories();
            const responseCategories = Array.isArray(catRes?.data) ? catRes.data : [];
            setCategories(responseCategories);
        } catch (err) {
            console.error("Error fetching categories", err);
            setCategories([]);
        } finally {
            setCategoriesLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDocuments();
        fetchCategories();
    }, [fetchDocuments, fetchCategories]);

    useEffect(() => {
        if (!selectedMachines || selectedMachines.length === 0) return;
        setMachines((prev) => {
            const map = new Map(prev.map((m) => [m.machine_id, m]));
            selectedMachines.forEach((machine) => {
                if (machine?.machine_id) {
                    map.set(machine.machine_id, machine);
                }
            });
            return Array.from(map.values());
        });
    }, [selectedMachines]);

    useEffect(() => {
        if (!openDialog) return;
        setMachineSearch("");
        fetchMachineResults(1, "");
    }, [openDialog]);

    useEffect(() => {
        if (!openDialog) return;
        const ids = selectedMachines.map((machine) => machine?.machine_id).filter(Boolean);
        if (ids.length === 0) {
            setAudienceOptions([]);
            return;
        }
        let isCancelled = false;
        const loadAudience = async () => {
            try {
                setAudienceLoading(true);
                const resp = await getDocumentAudience({ machine_ids: ids });
                if (!isCancelled) {
                    setAudienceOptions(Array.isArray(resp?.data) ? resp.data : []);
                }
            } catch (err) {
                console.error(err);
                if (!isCancelled) setAudienceOptions([]);
            } finally {
                if (!isCancelled) setAudienceLoading(false);
            }
        };
        loadAudience();
        return () => {
            isCancelled = true;
        };
    }, [openDialog, selectedMachines]);

    const fetchMachineResults = async (page = 1, q = "") => {
        try {
            setMachinesLoading(true);
            const resp = await searchMachinesApi(q, page, 50);
            const rows = Array.isArray(resp?.data) ? resp.data : [];
            const total = resp?.pagination?.total || 0;
            setMachines((prev) => {
                if (page === 1) return rows;
                const map = new Map(prev.map((m) => [m.machine_id, m]));
                rows.forEach((machine) => {
                    if (machine?.machine_id) {
                        map.set(machine.machine_id, machine);
                    }
                });
                return Array.from(map.values());
            });
            setMachineHasMore(page * 50 < total);
            setMachinePage(page);
        } catch (err) {
            console.error(err);
        } finally {
            setMachinesLoading(false);
        }
    };

    const handleSaveDocument = async () => {
        logDocumentUploadEvent("save_clicked", {
            isEditing,
            attachment_type: newDoc.attachment_type,
            selected_files: selectedDocumentFiles.length,
            site_id: formSiteNumber || selectedSite || null,
        });
        
        const resolvedSite =
            (formSiteNumber ? scopedSiteData.find((i) => i.customer_site_number === formSiteNumber) : null) ||
            selectedSiteRecord ||
            scopedSiteData[0] ||
            null;
        const site_id = resolvedSite?.site_id;
        logDocumentUploadEvent("site_resolved", {
            form_site_number: formSiteNumber || null,
            selected_site: selectedSite || null,
            resolved_site_id: site_id || null,
            resolved_site_number: resolvedSite?.customer_site_number || null,
            scoped_site_count: Array.isArray(scopedSiteData) ? scopedSiteData.length : 0,
        });
        const stepOneValid = validateStepOne();
        if (!stepOneValid) {
            logDocumentUploadEvent("step_one_validation_blocked", {
                errors: Object.keys(error || {}),
                form_errors: formErrors,
            });
            setIsSubmittingDocument(false);
            setUploadPhase("idle");
            return;
        }

        if (!site_id) {
            const siteError = t('UI_TEXT.SITE_REQUIRED') || 'Site is required';
            logDocumentUploadEvent("site_validation_blocked", {
                message: siteError,
                form_site_number: formSiteNumber || null,
                selected_site: selectedSite || null,
            });
            setError((prev) => ({ ...prev, site: siteError }));
            setFormErrors([siteError]);
            setIsSubmittingDocument(false);
            setUploadPhase("idle");
            return;
        }

        const hasFileUploadRows = !isEditing && newDoc.attachment_type === "file" && selectedDocumentFiles.length > 0;
        const hasUrlUploadRows = !isEditing && newDoc.attachment_type === "url" && urlDocumentRows.length > 0;

        if ((hasFileUploadRows || hasUrlUploadRows) && !validateNextPage()) {
            logDocumentUploadEvent("step_two_validation_blocked", {
                selected_files: hasUrlUploadRows ? urlDocumentRows.length : selectedDocumentFiles.length,
                validation_keys: Object.keys(validationErrors || {}),
            });
            setUploadTabIndex(1);
            setIsSubmittingDocument(false);
            setUploadPhase("idle");
            return;
        }

        if (isEditing && !editDocId) {
            Swal.fire({
                icon: "error",
                title: t('ALERT_MESSAGES.ERROR'),
                text: t('ALERT_MESSAGES.SOMETHING_WRONG_PLEASE_TRY_AGAIN'),
            });
            setIsSubmittingDocument(false);
            setUploadPhase("idle");
            return;
        }

        if (!isEditing && newDoc.attachment_type === "url") {
            const urlMetadataPayload = urlDocumentRows.map((row) => buildUrlUploadMetadata(row, site_id));
            const formData = new FormData();
            formData.append("name", urlMetadataPayload[0]?.name || "");
            formData.append("description", urlMetadataPayload[0]?.description || "");
            formData.append("attachment_type", "url");
            formData.append("site_id", site_id);
            formData.append("category_ids", JSON.stringify(urlMetadataPayload[0]?.category_ids || []));
            formData.append("new_categories", JSON.stringify(urlMetadataPayload[0]?.new_categories || []));
            formData.append("assigned_machines", JSON.stringify(urlMetadataPayload[0]?.assigned_machines || []));
            formData.append("visibility_scope", urlMetadataPayload[0]?.visibility_scope || visibilityScope);
            formData.append("visibility_targets", JSON.stringify(urlMetadataPayload[0]?.visibility_targets || []));
            formData.append("visibility_status", urlMetadataPayload[0]?.visibility_status || newDoc.status || "private");
            formData.append("file_metadata", JSON.stringify(urlMetadataPayload));
            formData.append(
                "batch_settings",
                JSON.stringify({
                    rollback_mode: "partial",
                    send_notification: uploadNotificationsEnabled,
                    total_files: urlDocumentRows.length,
                    total_size: 0,
                })
            );

            try {
                logDocumentUploadEvent("metadata_submit_start", {
                    selected_files: urlDocumentRows.length,
                    uploaded_files: urlMetadataPayload.length,
                    total_size: 0,
                    rollback_mode: "partial",
                    send_notification: uploadNotificationsEnabled,
                    attachment_type: "url",
                });
                setUploadPhase("finalizing");
                setIsSubmittingDocument(true);
                setUploadProgressItems((prev) =>
                    prev.map((item) => ({
                        ...item,
                        percent: 50,
                        status: "preparing",
                        message: "Saving URL document",
                    }))
                );
                const resp = await uploadDocument(formData);
                if (resp?.status) {
                    const batchData = resp?.data?.batch || null;
                    const backendResults = Array.isArray(resp?.data?.results) ? resp.data.results : [];
                    const backendSuccessCount = backendResults.filter((item) => getUploadResultStatus(item?.status) === "success").length;
                    const backendFailureCount = backendResults.filter((item) => getUploadResultStatus(item?.status) === "failed").length;
                    const resolvedFailureCount = backendFailureCount || Number(batchData?.failure_count || 0);
                    const resolvedSuccessCount =
                        backendSuccessCount ||
                        Number(batchData?.success_count || 0) ||
                        Math.max(0, urlDocumentRows.length - resolvedFailureCount);
                    const combinedBatch = {
                        ...(batchData || {}),
                        total_files: urlDocumentRows.length,
                        success_count: resolvedSuccessCount,
                        failure_count: resolvedFailureCount,
                    };
                    const reportResults = backendResults.length > 0
                        ? backendResults
                        : urlMetadataPayload.map((item, index) => ({
                            status: index < resolvedFailureCount ? false : true,
                            name: item.attachment || item.name || `URL ${index + 1}`,
                            attachment: item.attachment || "",
                            message: index < resolvedFailureCount ? "Failed" : `${item.name || "URL document"} saved`,
                        }));
                    logDocumentUploadEvent("metadata_submit_success", {
                        batch_id: combinedBatch.batch_id || batchData?.batch_id || null,
                        success_count: combinedBatch.success_count,
                        failure_count: combinedBatch.failure_count,
                        attachment_type: "url",
                    });
                    retryBatchContextRef.current = captureFailedBatchContext(reportResults, {
                        attachment_type: "url",
                        urlDocumentRows,
                        bulkFileDetails,
                        selectedDocCategories,
                        selectedMachines,
                        visibilityScope,
                        selectedAudienceCustomers,
                        selectedAudienceSites,
                        formSiteNumber,
                        newDoc,
                        uploadNotificationsEnabled,
                    });
                    setUploadProgressItems((prev) =>
                        prev.map((item, index) => {
                            const result = backendResults[index] || {};
                            const resultStatus = getUploadResultStatus(result?.status);
                            const isFailed = resultStatus === "failed";
                            return {
                                ...item,
                                percent: 100,
                                status: isFailed ? "failed" : "completed",
                                message: isFailed
                                    ? result?.error_message || result?.message || "Failed"
                                    : result?.message || "Completed",
                            };
                        })
                    );
                    setUploadPhase("done");
                    await fetchDocuments();
                    fetchCategories();
                    resetFormState();
                    setOpenDialog(false);
                    const result = await Swal.fire({
                        icon: combinedBatch.failure_count > 0 ? "warning" : "success",
                        title: "Document Upload Completed",
                        html: buildBatchUploadReportHtml(combinedBatch, reportResults, { itemLabel: "URL" }),
                        confirmButtonText: "Close",
                        showDenyButton: combinedBatch.failure_count > 0,
                        denyButtonText: "Retry Failed",
                        width: 900,
                    });
                    if (result?.isDenied) {
                        restoreFailedBatchContext(retryBatchContextRef.current);
                    } else {
                        retryBatchContextRef.current = null;
                    }
                    return;
                }
                const msg = String(resp?.message || resp?.error_message || t('ALERT_MESSAGES.SOMETHING_WRONG_PLEASE_TRY_AGAIN'));
                logDocumentUploadEvent("metadata_submit_failed", {
                    reason: "response_not_ok",
                    message: msg,
                    attachment_type: "url",
                });
                throw new Error(msg);
            } catch (err) {
                setUploadPhase("error");
                setUploadProgressItems((prev) =>
                    prev.map((item) => ({
                        ...item,
                        percent: 100,
                        status: "failed",
                        message: err?.response?.data?.message || err?.message || "Failed",
                    }))
                );
                logDocumentUploadEvent("metadata_submit_error", {
                    error: err?.message || String(err),
                    attachment_type: "url",
                });
                console.error(err);
                const msg = String(err?.response?.data?.message || err?.message || t('ALERT_MESSAGES.SOMETHING_WRONG_PLEASE_TRY_AGAIN'));
                Swal.fire({
                    icon: "error",
                    title: t("ALERT_MESSAGES.ERROR"),
                    text: msg,
                });
                return;
            } finally {
                logDocumentUploadEvent("metadata_submit_finished", { isSubmitting: false, attachment_type: "url" });
                setIsSubmittingDocument(false);
            }
        }

        if (!isEditing && newDoc.attachment_type === "file" && selectedDocumentFiles.length > 0) {
            if (totalSelectedFileSize > MAX_TOTAL_UPLOAD_SIZE) {
                const sizeMessage = `Total selected files must not exceed ${formatFileSize(MAX_TOTAL_UPLOAD_SIZE)}`;
                logDocumentUploadEvent("validation_failed", {
                    reason: "max_total_size",
                    total_selected_size: totalSelectedFileSize,
                    max_size: MAX_TOTAL_UPLOAD_SIZE,
                });
                setError((prevErr) => ({ ...prevErr, file: sizeMessage }));
                Swal.fire({
                    icon: "error",
                    title: t("ALERT_MESSAGES.ERROR"),
                    text: sizeMessage,
                });
                setIsSubmittingDocument(false);
                setUploadPhase("idle");
                return;
            }

            const updateUploadProgress = (fileKey, patch = {}) => {
                setUploadProgressItems((prev) =>
                    prev.map((item) =>
                        item.key === fileKey
                            ? {
                                ...item,
                                ...patch,
                            }
                            : item
                    )
                );
            };

            const fileMetadataPayload = [];
            const directUploadFailures = [];
            const existingCategoryIds = selectedDocCategories.filter((cat) => cat.id).map((cat) => cat.id);
            const newCategoryNames = selectedDocCategories.filter((cat) => !cat.id).map((cat) => cat.label);
            const selectedMachineIds = selectedMachines.map((machine) => machine.machine_id);
            const commonVisibilityTargets = visibilityScope === "custom" ? buildVisibilityPayload() : [];

            for (let index = 0; index < selectedDocumentFiles.length; index += 1) {
                const file = selectedDocumentFiles[index];
                const fileKey = getBulkFileKey(file);
                const fileDetails = bulkFileDetails[fileKey] || {};
                const fileMetadata = buildFileUploadMetadata(file, site_id);
                const resolvedDocumentName = String(fileMetadata.name || "").trim() || getFileBaseName(file?.name || "");
                const fileExtension = (() => {
                    const name = String(file?.name || "");
                    const dotIndex = name.lastIndexOf(".");
                    return dotIndex >= 0 ? name.slice(dotIndex) : "";
                })();
                const storageFilename = `${getFileBaseName(resolvedDocumentName)}${fileExtension}`;

                try {
                    updateUploadProgress(fileKey, {
                        name: resolvedDocumentName,
                        percent: 6,
                        status: "preparing",
                        message: "Preparing upload",
                    });
                    logDocumentUploadEvent("presign_request", {
                        index,
                        file_name: file?.name || null,
                        resolved_name: resolvedDocumentName,
                        size: Number(file?.size) || 0,
                    });
                    const presignResponse = await getDocumentUploadUrl({
                        filename: storageFilename,
                        content_type: file?.type || "application/octet-stream",
                        size_bytes: Number(file?.size) || 0,
                    });
                    const uploadUrl = presignResponse?.data?.upload_url;
                    const attachment = presignResponse?.data?.attachment;
                    if (!uploadUrl || !attachment) {
                        throw new Error("Unable to prepare upload");
                    }

                    await uploadFileToPresignedUrl(
                        uploadUrl,
                        file,
                        file?.type || "application/octet-stream",
                        (percent) => {
                            const displayPercent = Math.max(6, Math.min(94, percent));
                            updateUploadProgress(fileKey, {
                                percent: displayPercent,
                                status: "uploading",
                                message: `Uploading ${displayPercent}%`,
                            });
                        }
                    );
                    logDocumentUploadEvent("presign_upload_success", {
                        index,
                        file_name: file?.name || null,
                        resolved_name: resolvedDocumentName,
                    });
                    updateUploadProgress(fileKey, {
                        percent: 100,
                        status: "uploaded",
                        message: "Uploaded",
                    });

                    fileMetadataPayload.push({
                        name: resolvedDocumentName,
                        description: getRowDescriptionValue(fileDetails, newDoc.description ?? ""),
                        site_id,
                        attachment_type: "file",
                        attachment,
                        assigned_machines: (fileDetails.machines || selectedMachines).map((machine) => machine.machine_id),
                        visibility_scope: fileDetails.visibilityScope || visibilityScope,
                        visibility_targets:
                            (fileDetails.visibilityScope || visibilityScope) === "custom"
                                ? commonVisibilityTargets
                                : [],
                        category_ids: getAppliedRowCategories(fileDetails)
                            .filter((cat) => cat?.id)
                            .map((cat) => cat.id),
                        new_categories: getAppliedRowCategories(fileDetails)
                            .filter((cat) => !cat?.id)
                            .map((cat) => cat.label),
                        visibility_status: fileDetails.status || newDoc.status || "private",
                    });
                } catch (error) {
                    logDocumentUploadEvent("presign_upload_failed", {
                        index,
                        file_name: file?.name || null,
                        resolved_name: resolvedDocumentName,
                        error: error?.message || String(error),
                    });
                    updateUploadProgress(fileKey, {
                        percent: 100,
                        status: "failed",
                        message: normalizeUploadErrorMessage(error?.message),
                    });
                    directUploadFailures.push({
                        index,
                        status: false,
                        file_name: file?.name || resolvedDocumentName || null,
                        error_message: normalizeUploadErrorMessage(error?.message),
                    });
                }
            }

            if (fileMetadataPayload.length === 0) {
                logDocumentUploadEvent("upload_aborted", {
                    reason: "no_files_uploaded",
                    failures: directUploadFailures.length,
                });
                retryBatchContextRef.current = captureFailedBatchContext(directUploadFailures, {
                    attachment_type: "file",
                    selectedDocumentFiles,
                    bulkFileDetails,
                    selectedDocCategories,
                    selectedMachines,
                    visibilityScope,
                    selectedAudienceCustomers,
                    selectedAudienceSites,
                    formSiteNumber,
                    newDoc,
                    uploadNotificationsEnabled,
                });
                const retryResult = await Swal.fire({
                    icon: "error",
                    title: t("ALERT_MESSAGES.ERROR"),
                    text: directUploadFailures[0]?.error_message || "No files could be uploaded.",
                    confirmButtonText: "Close",
                    showDenyButton: directUploadFailures.length > 0,
                    denyButtonText: "Retry Failed",
                    width: 900,
                });
                if (retryResult?.isDenied) {
                    restoreFailedBatchContext(retryBatchContextRef.current);
                } else {
                    retryBatchContextRef.current = null;
                }
                setIsSubmittingDocument(false);
                return;
            }

            const formData = new FormData();
            formData.append("name", fileMetadataPayload[0]?.name || getFileBaseName(selectedDocumentFiles[0]?.name || ""));
            formData.append("description", fileMetadataPayload[0]?.description || "");
            formData.append("attachment_type", "file");
            formData.append("site_id", site_id);
            formData.append(
                "category_ids",
                JSON.stringify(fileMetadataPayload[0]?.category_ids || [])
            );
            formData.append(
                "new_categories",
                JSON.stringify(fileMetadataPayload[0]?.new_categories || [])
            );
            formData.append(
                "assigned_machines",
                JSON.stringify(fileMetadataPayload[0]?.assigned_machines || [])
            );
            formData.append("visibility_scope", fileMetadataPayload[0]?.visibility_scope || visibilityScope);
            formData.append(
                "visibility_targets",
                JSON.stringify(fileMetadataPayload[0]?.visibility_targets || [])
            );
            formData.append("visibility_status", fileMetadataPayload[0]?.visibility_status || newDoc.status || "private");
            formData.append("file_metadata", JSON.stringify(fileMetadataPayload));
            formData.append(
                "batch_settings",
                JSON.stringify({
                    rollback_mode: "partial",
                    send_notification: uploadNotificationsEnabled,
                    total_files: selectedDocumentFiles.length,
                    total_size: totalSelectedFileSize,
                })
            );

            try {
                logDocumentUploadEvent("metadata_submit_start", {
                    selected_files: selectedDocumentFiles.length,
                    uploaded_files: fileMetadataPayload.length,
                    total_size: totalSelectedFileSize,
                    rollback_mode: "partial",
                    send_notification: uploadNotificationsEnabled,
                });
                setUploadPhase("finalizing");
                setIsSubmittingDocument(true);
                const resp = await uploadDocument(formData);
                if (resp?.status) {
                    const batchData = resp?.data?.batch || null;
                    const backendResults = Array.isArray(resp?.data?.results) ? resp.data.results : [];
                    const combinedResults = [...directUploadFailures, ...backendResults];
                    const combinedSuccessCount = combinedResults.filter((item) => getUploadResultStatus(item?.status) === "success").length;
                    const combinedFailureCount = combinedResults.filter((item) => getUploadResultStatus(item?.status) === "failed").length;
                    const resolvedFailureCount = combinedFailureCount || Number(batchData?.failure_count || 0);
                    const resolvedSuccessCount =
                        combinedSuccessCount ||
                        Number(batchData?.success_count || 0) ||
                        Math.max(0, selectedDocumentFiles.length - resolvedFailureCount);
                    const combinedBatch = {
                        ...(batchData || {}),
                        total_files: selectedDocumentFiles.length,
                        success_count: resolvedSuccessCount,
                        failure_count: resolvedFailureCount,
                    };
                    logDocumentUploadEvent("metadata_submit_success", {
                        batch_id: combinedBatch.batch_id || batchData?.batch_id || null,
                        success_count: combinedBatch.success_count,
                        failure_count: combinedBatch.failure_count,
                    });
                    retryBatchContextRef.current = captureFailedBatchContext(combinedResults, {
                        attachment_type: "file",
                        selectedDocumentFiles,
                        bulkFileDetails,
                        selectedDocCategories,
                        selectedMachines,
                        visibilityScope,
                        selectedAudienceCustomers,
                        selectedAudienceSites,
                        formSiteNumber,
                        newDoc,
                        uploadNotificationsEnabled,
                    });
                    setUploadProgressItems((prev) =>
                        prev.map((item) => ({
                            ...item,
                            percent: 100,
                            status: item.status === "failed" ? "failed" : "completed",
                            message: item.status === "failed" ? item.message : "Completed",
                        }))
                    );
                    setUploadPhase("done");
                    await fetchDocuments();
                    fetchCategories();
                    resetFormState();
                    setOpenDialog(false);
                    const useBatchReport = shouldShowBatchUploadReport(selectedDocumentFiles.length, combinedBatch, combinedResults);
                    if (useBatchReport) {
                        const result = await Swal.fire({
                            icon: combinedBatch.failure_count > 0 ? "warning" : "success",
                            title: "Document Upload Completed",
                            html: buildBatchUploadReportHtml(combinedBatch, combinedResults),
                            confirmButtonText: "Close",
                            showDenyButton: combinedBatch.failure_count > 0,
                            denyButtonText: "Retry Failed",
                            width: 900,
                        });
                        if (result?.isDenied) {
                            restoreFailedBatchContext(retryBatchContextRef.current);
                        } else {
                            retryBatchContextRef.current = null;
                        }
                    } else {
                        const singleFailureMessage = normalizeUploadErrorMessage(
                            combinedResults[0]?.error_message || combinedResults[0]?.message || "",
                            "The document could not be uploaded. Please review the details and try again."
                        );
                        const result = await Swal.fire({
                            icon: combinedBatch.failure_count > 0 ? "error" : "success",
                            title: combinedBatch.failure_count > 0 ? t("ALERT_MESSAGES.ERROR") : t("ALERT_MESSAGES.SUCCESS"),
                            text: combinedBatch.failure_count > 0
                                ? singleFailureMessage
                                : t("ALERT_MESSAGES.DOCUMENT_UPLOADED_SUCCESSFULLY", "Document uploaded successfully"),
                            confirmButtonText: "Close",
                            showDenyButton: combinedBatch.failure_count > 0,
                            denyButtonText: "Retry Failed",
                            width: 520,
                        });
                        if (result?.isDenied) {
                            restoreFailedBatchContext(retryBatchContextRef.current);
                        } else {
                            retryBatchContextRef.current = null;
                        }
                    }
                    return;
                }
                const msg = String(resp?.message || resp?.error_message || t('ALERT_MESSAGES.SOMETHING_WRONG_PLEASE_TRY_AGAIN'));
                logDocumentUploadEvent("metadata_submit_failed", {
                    reason: "response_not_ok",
                    message: msg,
                });
                throw new Error(msg);
            } catch (err) {
                setUploadPhase("error");
                logDocumentUploadEvent("metadata_submit_error", {
                    error: err?.message || String(err),
                });
                console.error(err);
                const msg = normalizeUploadErrorMessage(
                    err?.response?.data?.message || err?.message || t('ALERT_MESSAGES.SOMETHING_WRONG_PLEASE_TRY_AGAIN'),
                    t('ALERT_MESSAGES.SOMETHING_WRONG_PLEASE_TRY_AGAIN')
                );
                const singleFailureResults = [{
                    status: false,
                    file_name: urlDocumentRows[0]?.name || newDoc.name || "Document",
                    error_message: msg,
                }];
                retryBatchContextRef.current = captureFailedBatchContext(singleFailureResults, {
                    attachment_type: "file",
                    selectedDocumentFiles,
                    bulkFileDetails,
                    selectedDocCategories,
                    selectedMachines,
                    visibilityScope,
                    selectedAudienceCustomers,
                    selectedAudienceSites,
                    formSiteNumber,
                    newDoc,
                    uploadNotificationsEnabled,
                });
                const result = await Swal.fire({
                    icon: "error",
                    title: "Document Upload Completed",
                    html: buildBatchUploadReportHtml(
                        {
                            total_files: selectedDocumentFiles.length || 1,
                            success_count: 0,
                            failure_count: 1,
                        },
                        singleFailureResults
                    ),
                    confirmButtonText: "Close",
                    showDenyButton: true,
                    denyButtonText: "Retry Failed",
                    width: 900,
                });
                if (result?.isDenied) {
                    restoreFailedBatchContext(retryBatchContextRef.current);
                } else {
                    retryBatchContextRef.current = null;
                }
                return;
            } finally {
                logDocumentUploadEvent("metadata_submit_finished", { isSubmitting: false });
                setIsSubmittingDocument(false);
            }
        }

        const formData = new FormData();
        const firstFile = selectedDocumentFiles[0];
        const firstFileDetails = firstFile ? (bulkFileDetails[getBulkFileKey(firstFile)] || {}) : {};
        const firstFileHasOwnName = Object.prototype.hasOwnProperty.call(firstFileDetails, "name");
        const documentName =
            firstFileHasOwnName
                ? getRowNameValue(firstFileDetails, "")
                : (
                    String(newDoc.name || "").trim() ||
                    (firstFile ? getFileBaseName(firstFile.name) : "")
                );
        const documentDescription = getRowDescriptionValue(firstFileDetails, newDoc.description ?? "");
        const documentCategories = getAppliedRowCategories(firstFileDetails);
        const documentMachines = firstFileDetails.machines || selectedMachines;
        const documentVisibilityScope = firstFileDetails.visibilityScope || visibilityScope;
        const isBatchUpload = !isEditing && selectedDocumentFiles.length > 1;
        const uploadFilesField = isBatchUpload ? "files" : "file";
        const filesToUpload = isBatchUpload ? selectedDocumentFiles : selectedDocumentFiles.slice(0, 1);
        const fileMetadata = filesToUpload.map((file) => buildFileUploadMetadata(file, site_id));
        const batchSettings = {
            rollback_mode: "partial",
            send_notification: uploadNotificationsEnabled,
            total_files: selectedDocumentFiles.length,
            total_size: totalSelectedFileSize,
        };

        formData.append("name", documentName);
        formData.append("description", documentDescription || "");
        formData.append("attachment_type", newDoc.attachment_type);
        if (newDoc.attachment_type === 'file' && filesToUpload.length > 0) {
            filesToUpload.forEach((file) => {
                formData.append(uploadFilesField, file);
            });
        }
        if (newDoc.attachment_type === 'url') {
            const urlAttachment = String(newDoc.url ?? editingDocument?.attachment ?? "").trim();
            if (urlAttachment) {
                formData.append("attachment", urlAttachment);
            }
        }
        formData.append("site_id", site_id);
        const existingCategoryIds = documentCategories.filter((cat) => cat.id).map((cat) => cat.id);
        const newCategoryNames = documentCategories.filter((cat) => !cat.id).map((cat) => cat.label);
        formData.append("category_ids", JSON.stringify(existingCategoryIds));
        formData.append("new_categories", JSON.stringify(newCategoryNames));
        const selectedMachineIds = documentMachines.map((machine) => machine.machine_id);
        formData.append("assigned_machines", selectedMachineIds);
        formData.append("visibility_scope", documentVisibilityScope);
        formData.append("visibility_targets", JSON.stringify(buildVisibilityPayload()));
        formData.append("visibility_status", newDoc.status || "private");
        formData.append("file_metadata", JSON.stringify(fileMetadata));
        formData.append("batch_settings", JSON.stringify(batchSettings));

        try {
            logDocumentUploadEvent("direct_submit_start", {
                is_editing: isEditing,
                selected_files: selectedDocumentFiles.length,
                batch_mode: isBatchUpload,
                total_size: totalSelectedFileSize,
            });
            setIsSubmittingDocument(true);
            const resp = isEditing
                ? await updateDocument(editDocId, formData)
                : await uploadDocument(formData);

            if (resp?.status) {
                const batchData = resp?.data?.batch || null;
                const batchResults = Array.isArray(resp?.data?.results) ? resp.data.results : [];
                const isBatchResponse = !isEditing && shouldShowBatchUploadReport(selectedDocumentFiles.length, batchData, batchResults);
                const batchSuccessCount = batchResults.filter((item) => getUploadResultStatus(item?.status) === "success").length;
                const batchFailureCount = batchResults.filter((item) => getUploadResultStatus(item?.status) === "failed").length;
                const reportTotalCount = Number(batchData?.total_files || batchResults.length || selectedDocumentFiles.length || 1);
                const reportFailureCount = batchFailureCount || Number(batchData?.failure_count || 0);
                const reportSuccessCount =
                    batchSuccessCount ||
                    Number(batchData?.success_count || 0) ||
                    Math.max(0, reportTotalCount - reportFailureCount);
                const reportBatch = {
                    ...(batchData || {}),
                    total_files: reportTotalCount,
                    success_count: reportSuccessCount,
                    failure_count: reportFailureCount,
                };
                logDocumentUploadEvent("direct_submit_success", {
                    response_type: isBatchResponse ? "batch" : "single",
                    batch_id: batchData?.batch_id || null,
                    success_count: reportBatch.success_count,
                    failure_count: reportBatch.failure_count,
                });
                await fetchDocuments();
                fetchCategories();
                resetFormState();
                setOpenDialog(false);
                if (isBatchResponse) {
                    const result = await Swal.fire({
                        icon: reportBatch.failure_count > 0 ? "warning" : "success",
                        title: "Documents upload completed",
                        html: buildBatchUploadReportHtml(reportBatch, batchResults),
                        confirmButtonText: "Close",
                        showDenyButton: reportBatch.failure_count > 0,
                        denyButtonText: "Retry Failed",
                        width: 900,
                    });
                    if (result?.isDenied) {
                        restoreFailedBatchContext(retryBatchContextRef.current);
                    } else {
                        retryBatchContextRef.current = null;
                    }
                } else {
                    const singleFailureMessage = normalizeUploadErrorMessage(
                        batchResults[0]?.error_message || batchResults[0]?.message || batchData?.error_message || batchData?.message || "",
                        "The document could not be uploaded. Please review the details and try again."
                    );
                    const result = await Swal.fire({
                        icon: reportBatch.failure_count > 0 ? "error" : "success",
                        title: reportBatch.failure_count > 0 ? t('ALERT_MESSAGES.ERROR') : t('ALERT_MESSAGES.SUCCESS'),
                        text: reportBatch.failure_count > 0
                            ? singleFailureMessage
                            : isEditing
                                ? t('ALERT_MESSAGES.DOCUMENT_UPDATED', 'Document updated successfully')
                                : t('ALERT_MESSAGES.DOCUMENT_UPLOADED_SUCCESSFULLY', 'Document uploaded successfully'),
                        confirmButtonText: "Close",
                        showDenyButton: reportBatch.failure_count > 0,
                        denyButtonText: "Retry Failed",
                        width: 520,
                    });
                    if (result?.isDenied) {
                        restoreFailedBatchContext(retryBatchContextRef.current);
                    } else {
                        retryBatchContextRef.current = null;
                    }
                }
            } else {
                const respMessage = String(resp?.message || resp?.error_message || "");
                const isDuplicate =
                    resp?.error_code === "DUPLICATE_DOCUMENT_NAME" ||
                    resp?.error?.code === "23505" ||
                    resp?.error?.constraint === "documents_name_key" ||
                    respMessage.toLowerCase().includes("duplicate") ||
                    respMessage.toLowerCase().includes("already exists");
                if (isDuplicate) {
                    const msg = normalizeUploadErrorMessage(
                        respMessage || t('ALERT_MESSAGES.DUPLICATE_DOCUMENT_NAME_CATEGORY'),
                        t('ALERT_MESSAGES.DUPLICATE_DOCUMENT_NAME_CATEGORY')
                    );
                    logDocumentUploadEvent("direct_submit_failed", {
                        reason: "duplicate",
                        message: msg,
                    });
                    setUploadPhase("error");
                    setError((prevErrors) => ({ ...prevErrors, name: msg }));
                    setFormErrors([msg]);
                    Swal.fire({
                        icon: "error",
                        title: t('ALERT_MESSAGES.ERROR'),
                        text: msg,
                    });
                } else {
                    const msg = normalizeUploadErrorMessage(
                        respMessage || t('ALERT_MESSAGES.SOMETHING_WRONG_PLEASE_TRY_AGAIN'),
                        t('ALERT_MESSAGES.SOMETHING_WRONG_PLEASE_TRY_AGAIN')
                    );
                    if (!isEditing && selectedDocumentFiles.length <= 1) {
                        const singleFailureResults = [{
                            status: false,
                            file_name: selectedDocumentFiles[0]?.name || documentName || "Document",
                            error_message: msg,
                        }];
                        retryBatchContextRef.current = captureFailedBatchContext(singleFailureResults, {
                            attachment_type: "file",
                            selectedDocumentFiles,
                            bulkFileDetails,
                            selectedDocCategories,
                            selectedMachines,
                            visibilityScope,
                            selectedAudienceCustomers,
                            selectedAudienceSites,
                            formSiteNumber,
                            newDoc,
                            uploadNotificationsEnabled,
                        });
                        const result = await Swal.fire({
                            icon: "error",
                            title: "Document Upload Completed",
                            html: buildBatchUploadReportHtml(
                                {
                                    total_files: selectedDocumentFiles.length || 1,
                                    success_count: 0,
                                    failure_count: 1,
                                },
                                singleFailureResults
                            ),
                            confirmButtonText: "Close",
                            showDenyButton: true,
                            denyButtonText: "Retry Failed",
                            width: 900,
                        });
                        if (result?.isDenied) {
                            restoreFailedBatchContext(retryBatchContextRef.current);
                        } else {
                            retryBatchContextRef.current = null;
                        }
                        return;
                    }
                    logDocumentUploadEvent("direct_submit_failed", {
                        reason: "response_error",
                        message: msg,
                    });
                    setUploadPhase("error");
                    Swal.fire({
                        icon: "error",
                        title: t('ALERT_MESSAGES.ERROR'),
                        text: msg,
                    });
                    setOpenDialog(false);
                    resetFormState();
                }
            }
        } catch (err) {
            console.error(err);
            setUploadPhase("error");
            logDocumentUploadEvent("direct_submit_error", {
                error: err?.message || String(err),
            });
            const statusCode = err.response?.status;
            const errMessage = String(err.response?.data?.message || "");
            const errorCode = err.response?.data?.error_code;
            const isDuplicate =
                (statusCode === 400 || statusCode === 501) &&
                (errorCode === "DUPLICATE_DOCUMENT_NAME" ||
                    err.response?.data?.error?.code === "23505" ||
                    err.response?.data?.error?.constraint === "documents_name_key" ||
                    errMessage.toLowerCase().includes("duplicate") ||
                    errMessage.toLowerCase().includes("already exists"));
            if (isDuplicate) {
                const msg = errMessage || t('ALERT_MESSAGES.DUPLICATE_DOCUMENT_NAME_CATEGORY');
                setError((prevErrors) => ({ ...prevErrors, name: msg }));
                setFormErrors([msg]);
                Swal.fire({
                    icon: "error",
                    title: t('ALERT_MESSAGES.ERROR'),
                    text: msg,
                });
            } else {
                setOpenDialog(false);
                resetFormState();
                Swal.fire({
                    icon: "error",
                    title: t('ALERT_MESSAGES.ERROR'),
                    text: t('ALERT_MESSAGES.SOMETHING_WRONG_PLEASE_TRY_AGAIN'),
                });
            }
        } finally {
            setIsSubmittingDocument(false);
        }
    };

    const handleStartDocumentUpload = () => {
        if (isSubmittingDocument) return;

        const existingNameMap = new Map(
            (Array.isArray(documents) ? documents : [])
                .filter((doc) => !isEditing || Number(doc?.id) !== Number(editDocId))
                .map((doc) => String(doc?.name || doc?.document_name || "").trim())
                .filter(Boolean)
                .map((name) => [normalizeDocumentNameKey(name), name])
        );
        const batchNameMap = new Map();
        const duplicateEntries = [];

                if (!isEditing) {
        selectedDocumentFiles.forEach((file) => {
            const fileKey = getBulkFileKey(file);
            const fileDetails = bulkFileDetails[fileKey] || {};
            const resolvedName = getRowNameValue(fileDetails, getFileBaseName(file?.name || ""));
            const normalizedName = normalizeDocumentNameKey(resolvedName);
            if (!normalizedName) return;

            if (existingNameMap.has(normalizedName)) {
                duplicateEntries.push({
                    fileKey,
                    fileName: resolvedName,
                    reason: "existing",
                    existingName: existingNameMap.get(normalizedName),
                });
                return;
            }

            if (batchNameMap.has(normalizedName)) {
                duplicateEntries.push({
                    fileKey,
                    fileName: resolvedName,
                    reason: "batch",
                    existingName: batchNameMap.get(normalizedName),
                });
                return;
            }

            batchNameMap.set(normalizedName, resolvedName);
        });
        }

        if (duplicateEntries.length > 0) {
            const duplicateMessage = "One or more document names already exist. Please rename the highlighted files before uploading.";
            setValidationErrors((prev) => {
                const next = { ...prev };
                duplicateEntries.forEach((entry) => {
                    next[entry.fileKey] = duplicateMessage;
                });
                return next;
            });
            setUploadPhase("idle");
            setIsSubmittingDocument(false);
            setUploadTabIndex(0);
            Swal.fire({
                icon: "error",
                title: t("ALERT_MESSAGES.ERROR"),
                html: `
                    <div style="text-align:left;">
                        <div style="margin-bottom:8px;">${duplicateMessage}</div>
                        <ul style="margin:0;padding-left:18px;">
                            ${duplicateEntries
                                .map((entry) => `<li>${entry.fileName}</li>`)
                                .join("")}
                        </ul>
                    </div>
                `,
            });
            return;
        }

        if (!validateNextPage()) {
            setUploadPhase("idle");
            setIsSubmittingDocument(false);
            setUploadTabIndex(1);
            return;
        }

        const progressItems = newDoc.attachment_type === "url"
            ? urlDocumentRows.map((row, index) => {
                const rowKey = row?.key || `url-${index}`;
                const rowDetails = bulkFileDetails[rowKey] || {};
                return {
                    key: rowKey,
                    name: String(rowDetails.name || row?.name || "").trim() || `URL document ${index + 1}`,
                    percent: 0,
                    status: "queued",
                    message: "Queued",
                };
            })
            : selectedDocumentFiles.map((file) => {
                const fileKey = getBulkFileKey(file);
                const fileDetails = bulkFileDetails[fileKey] || {};
                return {
                    key: fileKey,
                    name: getRowNameValue(fileDetails, getFileBaseName(file?.name || "")),
                    percent: 0,
                    status: "queued",
                    message: "Queued",
                };
            });

        setUploadProgressItems(progressItems);
        setUploadPhase("uploading");
        setIsSubmittingDocument(true);
        setUploadTabIndex(2);
        window.setTimeout(() => {
            if (uploadModalScrollRef.current) uploadModalScrollRef.current.scrollTop = 0;
        }, 0);
        window.setTimeout(() => {
            void handleSaveDocument();
        }, 0);
    };

    const handleDeleteDocument = async (id) => {
        if (!canDeleteDocuments) return;
        try {
            const confirmation = await Swal.fire({
                title: t('ALERT_MESSAGES.ARE_YOU_SURE'),
                text: t('ALERT_MESSAGES.DOCUMENT_PERMANENTLY_DELETED'),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: t('GENERAL.YES_DELETE_IT'),
            });

            if (!confirmation.isConfirmed) return;

            await deleteDocument(id);

            setDocuments((prevDocuments) => prevDocuments.filter((document) => document.id !== id));
            fetchCategories();
            if (viewDoc?.id === id) {
                handleCloseViewDocument();
            }

            Swal.fire({
                icon: "success",
                title: t('ALERT_MESSAGES.DELETED'),
                text: t('ALERT_MESSAGES.DOCUMENT_DELETED_SUCCESSFULLY'),
            });
        } catch (err) {
            console.error("Error deleting document:", err);
            Swal.fire({
                icon: "error",
                title: t('ALERT_MESSAGES.ERROR'),
                text: t('ALERT_MESSAGES.COULD_NOT_DELETE_DOCUMENT'),
            });
        }
    };

    const handleCloseModal = () => {
        resetFormState();
        setIsEditing(false);
        setEditDocId(null);
        setOpenDialog(false);
    };

    const handleEditOpen = async (row) => {
        setIsEditing(true);
        setEditDocId(row.id);
        setUploadTabIndex(0);
        setFormErrors([]);
        setError({});
        setValidationErrors({});
        const scope = row.visibility_scope || 'all';
        const targets = Array.isArray(row.visibility_targets) ? row.visibility_targets : [];
        const customerSelections = targets
            .filter((t) => t?.all_sites && t?.cus_id)
            .map((t) => ({
                cus_id: t.cus_id,
                customer_name: t.customer_name,
                customer_number: t.customer_number,
            }));
        const siteSelections = targets
            .filter((t) => !t?.all_sites && t?.site_id && t?.cus_id)
            .map((t) => ({
                row_id: getAudienceLocationRowId(t.cus_id, t.site_id),
                site_id: t.site_id,
                cus_id: t.cus_id,
                customer_site_name: t.customer_site_name,
                customer_site_number: t.customer_site_number,
                parent_customer_name: t.customer_name,
            }));
        setSelectedAudienceCustomers(customerSelections);
        setSelectedAudienceSites(siteSelections);
        setVisibilityScope(scope);
        const matchingSiteNumber =
            scopedSiteData.find((site) => Number(site.site_id) === Number(row.site_id))?.customer_site_number ||
            selectedSite ||
            "";
        setFormSiteNumber(matchingSiteNumber);

        try {
            const normalizedAssignedMachines = Array.isArray(row.assigned_machines) ? row.assigned_machines.filter(Boolean) : [];
            const normalizedMachineDetails = Array.isArray(row.machine_details) ? row.machine_details.filter(Boolean) : [];
            const hasMachineObjects = normalizedAssignedMachines.some((machine) => machine && typeof machine === "object");
            if (hasMachineObjects) {
                const uniqueMachines = Array.from(
                    new Map(
                        normalizedAssignedMachines
                            .map((machine) => [String(machine?.machine_id || machine?.id || machine?.value || ""), machine])
                            .filter(([key]) => key)
                    ).values()
                );
                setSelectedMachines(uniqueMachines);
            } else if (normalizedMachineDetails.length > 0) {
                setSelectedMachines(normalizedMachineDetails);
            } else if (normalizedAssignedMachines.length > 0) {
                const resp = await getMachinesByIds({
                    machines: normalizedAssignedMachines,
                    site_number: matchingSiteNumber,
                    site_id: row.site_id,
                    cus_id: row.cus_id,
                });
                if (resp?.status) {
                    const uniqueMachines = Array.from(
                        new Map((resp.data || []).map((m) => [m.machine_id, m])).values()
                    );
                    setSelectedMachines(uniqueMachines);
                } else {
                    setSelectedMachines([]);
                }
            } else {
                setSelectedMachines([]);
            }
        } catch (e) {
            setSelectedMachines([]);
        }

        const docCategories = Array.isArray(row.categories) ? row.categories : [];
        setSelectedDocCategories(
            docCategories.map((cat) => ({
                id: cat.category_id || cat.id || null,
                label: cat.category_name || cat.label || "",
                isNew: false,
            }))
        );
        setNewDoc({
            name: row.name,
            description: row.description || '',
            attachment_type: row.attachment_type || 'none',
            url: row.attachment_type === 'url' ? (row.attachment || '') : '',
            files: [],
            status: row.visibility_status || 'private',
        });
        setOpenDialog(true);
    };

    if (!canManageDocuments) {
        return <NotAuthorized />;
    }

    const modalTitle = isEditing
        ? t('UI_TEXT.EDIT_DOCUMENT', { defaultValue: 'Edit Document' })
        : t('UI_TEXT.ADD_NEW_DOCUMENT', { defaultValue: 'Add New Documents' });

    return (
        <div>
            <Tabs value={tabIndex} onChange={(event, value) => setTabIndex(value)} sx={{ mb: 2 }}>
                <Tab label={t('LABELS.DOCUMENTS') || 'Documents'} value={0} />
                <Tab label={t('LABELS.DOCUMENT_CATEGORIES') || 'Categories'} value={1} />
            </Tabs>

            {tabIndex === 0 && (
                documentsLoading ? (
                    <DatagridLoading
                        actionButtons={customButtons.length}
                        columns={6}
                        rows={6}
                    />
                ) : (
                    <Datagrid
                        data={documents}
                        columns={columns}
                        rowIdField="id"
                        customButtons={customButtons}
                        checkboxSelection={true}
                        onSelect={(ids) => setSelectedRows(ids)}
                        rowClick
                        onRowClick={(params) => handleViewDocument(params.row)}
                        hideFilterFromColumnMenu
                        lockActiveColumnInManageColumns
                        preventHidingLastColumn
                    />
                )
            )}

            {tabIndex === 1 && (
                categoriesLoading ? (
                    <DatagridLoading
                        actionButtons={customButtonsCategory.length}
                        columns={4}
                        rows={6}
                    />
                ) : (
                    <Datagrid
                        data={categoryGridRows}
                        columns={categoryColumns}
                        rowIdField="id"
                        checkboxSelection={false}
                        rowClick
                        customButtons={customButtonsCategory}
                        onRowClick={(params) => handleOpenCategoryDetails(params.row)}
                        hideFilterFromColumnMenu
                        lockActiveColumnInManageColumns
                        preventHidingLastColumn
                    />
                )

            )}

            <ModalWrapper
                open={openDialog}
                setOpen={setOpenDialog}
                onClose={handleCloseModal}
                title={modalTitle}
                width="calc(70vw - 2cm)"
                contentRef={uploadModalScrollRef}
                content={
                    <Box sx={{ overflowY: "auto", p: 1 }}>
                        {formErrors.length > 0 && (
                            <Box sx={{ mb: 1.5 }}>
                                <Alert severity="error">
                                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Please fix the following:</div>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {formErrors.map((msg, idx) => (<li key={idx}>{msg}</li>))}
                                    </ul>
                                </Alert>
                            </Box>
                        )}
                        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                            <Box sx={{ width: "100%", maxWidth: 640 }}>
                                <Box
                                    sx={{
                                        position: "relative",
                                        pt: 0.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 14,
                                            left: "calc(16.666% + 18px)",
                                            width: "calc(33.333% - 36px)",
                                            height: "1px",
                                            backgroundColor: "#c5c5c5",
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 14,
                                            left: "calc(50% + 18px)",
                                            width: "calc(33.333% - 36px)",
                                            height: "1px",
                                            backgroundColor: "#c5c5c5",
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                            alignItems: "start",
                                            position: "relative",
                                            zIndex: 1,
                                            columnGap: 1,
                                        }}
                                    >
                                        {[
                                            { step: 0, label: "Document Details" },
                                            { step: 1, label: "Machines & Visibility" },
                                            { step: 2, label: "Upload Progress" },
                                        ].map((item) => {
                                            const isActive = uploadTabIndex === item.step;
                                            const isDone = uploadTabIndex > item.step;
                                            return (
                                                <Box
                                                    key={item.step}
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "center",
                                                        gap: 1.25,
                                                        width: "100%",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 20,
                                                            height: 20,
                                                            borderRadius: "50%",
                                                            backgroundColor: isActive || isDone ? "#0b9bd3" : "#9e9e9e",
                                                            color: "#fff",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: 10,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {item.step + 1}
                                                    </Box>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            textAlign: "center",
                                                            fontWeight: isActive ? 600 : 400,
                                                            color: isActive ? "text.primary" : "text.secondary",
                                                        }}
                                                    >
                                                        {item.label}
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                            {!uploadTabIndex && (
                                <FormControl fullWidth size="small">
                                    <InputLabel id="attachment-type-label">Attachment Type</InputLabel>
                                    <Select
                                        labelId="attachment-type-label"
                                        label="Attachment Type"
                                        value={newDoc.attachment_type}
                                        onChange={(e) =>
                                            {
                                                const nextType = e.target.value;
                                                setNewDoc((prev) => ({
                                                    ...prev,
                                                    attachment_type: nextType,
                                                    files: nextType === "file" ? prev.files : [],
                                                }));
                                                if (nextType === "url") {
                                                    setUrlDocumentRows((prev) => (prev.length > 0 ? prev : [createBlankUrlRow()]));
                                                }
                                            }
                                        }
                                    >
                                        <MenuItem value="file">File Attachment</MenuItem>
                                        <MenuItem value="url">URL Attachment</MenuItem>
                                    </Select>
                                </FormControl>
                            )}

                            {/* {newDoc.attachment_type === "url" && !isEditing && uploadTabIndex === 0 && (
                                <Box sx={{ mt: 2.5 }}>
                                    <Box sx={{ mb: 2.5, p: 2, border: "1px solid #e9edf2", borderRadius: 1.5, backgroundColor: "#f8f9fa" }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                            Apply to all
                                        </Typography>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                                Machines
                                            </Typography> */}
                            {newDoc.attachment_type === "url" && !isEditing && uploadTabIndex === 0 && (
                                <Box sx={{ mt: 2.5 }}>
                                    <Box sx={{ mb: 2.5, p: 2, border: "1px solid #e9edf2", borderRadius: 1.5, backgroundColor: "#f8f9fa" }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                            Apply to all documents:
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
                                            <Box sx={{ minWidth: 200 }}>
                                                <Autocomplete
                                                    multiple
                                                    freeSolo
                                                    size="small"
                                                    options={categorySelectOptions}
                                                    value={selectedDocCategories}
                                                    onChange={(event, newValue) => {
                                                        const normalized = normalizeCategorySelection(newValue);
                                                        setSelectedDocCategories((prevSelected) => {
                                                            setBulkFileDetails((prev) => {
                                                                const next = { ...prev };
                                                                urlDocumentRows.forEach((row) => {
                                                                    next[row.key] = applyCategorySelectionChangeToRowDetails(next[row.key], prevSelected, normalized);
                                                                });
                                                                return next;
                                                            });
                                                            return normalized;
                                                        });
                                                        if (normalized.length > 0) {
                                                            setValidationErrors((prev) => {
                                                                const next = { ...prev };
                                                                urlDocumentRows.forEach((row) => {
                                                                    delete next[`${row.key}_category`];
                                                                });
                                                                return next;
                                                            });
                                                        }
                                                    }}
                                                    getOptionLabel={(option) => option.label || ""}
                                                    isOptionEqualToValue={isSameCategoryOption}
                                                    sx={bulkMultiSelectSx}
                                                    renderTags={(value, getTagProps) =>
                                                        renderScrollableTags(value, getTagProps, (option) => option.label || "", 44)
                                                    }
                                                    renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                            label="Category"
                                                        placeholder={selectedDocCategories.length > 0 ? "" : "Category"}
                                                    />
                                                )}
                                            />
                                            </Box>
                                            <Box sx={{ minWidth: 150 }}>
                                                <FormControl size="small" fullWidth>
                                                    <InputLabel>Status</InputLabel>
                                                    <Select
                                                        label="Status"
                                                        value={newDoc.status || "private"}
                                                        onChange={(e) => {
                                                            const nextStatus = e.target.value;
                                                            setNewDoc((prev) => ({ ...prev, status: nextStatus }));
                                                            setBulkFileDetails((prev) => {
                                                                const next = { ...prev };
                                                                urlDocumentRows.forEach((row) => {
                                                                    next[row.key] = {
                                                                        ...(next[row.key] || {}),
                                                                        status: nextStatus,
                                                                    };
                                                                });
                                                                return next;
                                                            });
                                                        }}
                                                    >
                                                        <MenuItem value="private">Private</MenuItem>
                                                        <MenuItem value="public">Public</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Box>
                                        {/* <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                                Machines
                                            </Typography>
                                            <Autocomplete
                                                size="small"
                                                multiple
                                                options={machines}
                                                disablePortal
                                                filterOptions={(x) => x}
                                                getOptionLabel={(machine) => `${machine.machine_number} - ${machine.machine_name}`}
                                                isOptionEqualToValue={(option, value) => option.machine_id === value.machine_id}
                                                value={selectedMachines}
                                                onChange={(event, newValue) => {
                                                    const nextMachines = newValue || [];
                                                    const previousMachines = selectedMachines || [];
                                                    const previousMachineIds = new Set(previousMachines.map((machine) => machine?.machine_id).filter(Boolean));
                                                    const nextMachineIds = new Set(nextMachines.map((machine) => machine?.machine_id).filter(Boolean));
                                                    const addedMachines = nextMachines.filter((machine) => machine?.machine_id && !previousMachineIds.has(machine.machine_id));
                                                    const removedMachines = previousMachines.filter((machine) => machine?.machine_id && !nextMachineIds.has(machine.machine_id));
                                                    setSelectedMachines(nextMachines);
                                                    setBulkFileDetails((prev) => {
                                                        const next = { ...prev };
                                                        urlDocumentRows.forEach((row) => {
                                                            const key = row.key;
                                                            const existingMachines = Array.isArray(next[key]?.machines) ? next[key].machines : [];
                                                            const mergedMachines = mergeMachineSelections(existingMachines, addedMachines);
                                                            next[key] = {
                                                                ...(next[key] || {}),
                                                                machines: removeMachinesFromSelection(mergedMachines, removedMachines),
                                                            };
                                                        });
                                                        return next;
                                                    });
                                                }}
                                                onInputChange={(e, value) => {
                                                    setMachineSearch(value);
                                                    clearTimeout(window.__md_search_timer__);
                                                    window.__md_search_timer__ = setTimeout(() => {
                                                        fetchMachineResults(1, value || "");
                                                    }, 300);
                                                }}
                                                sx={bulkMultiSelectSx}
                                                renderTags={(value, getTagProps) =>
                                                    renderScrollableTags(value, getTagProps, (machine) => `${machine.machine_number} - ${machine.machine_name}`)
                                                }
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        placeholder={selectedMachines.length > 0 ? "" : "Select machines..."}
                                                        error={!!validationErrors.machine}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            endAdornment: (
                                                                <>
                                                                    {machinesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                                    {params.InputProps.endAdornment}
                                                                </>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Box> */}
                                    </Box>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 1.5 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            Document URL
                                        </Typography>
                                        {!isEditing && (
                                            <Button variant="outlined" size="small" onClick={addUrlDocumentRow}>
                                                Add Row
                                            </Button>
                                        )}
                                    </Box>
                                    <Box sx={{ overflowX: "hidden", border: "1px solid #e9edf2", borderRadius: 1.5, minHeight: 120 }}>
                                        <Box sx={{ width: "100%", minWidth: 0 }}>
                                            <Box sx={{ display: "grid", gridTemplateColumns: `${uploadRowIconColumn} ${uploadRowNameColumn} ${uploadRowDescriptionColumn} ${uploadRowNameColumn} ${uploadRowCategoryColumn} ${uploadRowStatusColumn} ${uploadRowDeleteColumn}`, gap: 1.25, px: 1.5, py: 1, backgroundColor: "#f7f9fb", borderBottom: "1px solid #e9edf2", fontWeight: 700, alignItems: "center" }}>
                                                <span />
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Name</RequiredLabel></Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Description</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Document URL</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Category</RequiredLabel></Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Status</Typography>
                                                <span />
                                            </Box>
                                            {urlDocumentRows.map((row) => {
                                                const rowKey = row.key;
                                                const rowDetails = bulkFileDetails[rowKey] || {};
                                                const rowCategories = getAppliedRowCategories(rowDetails);
                                                const rowName = getRowNameValue(rowDetails, row.name || "");
                                                const rowDescription = getRowDescriptionValue(rowDetails, row.description || "");
                                                const rowUrl = String(rowDetails.url || row.url || "").trim();
                                                          const rowTypeLabel = "URL";
                                                          const rowTypeColors = { backgroundColor: "#e6f3ff", color: "#1976d2" };
                                                return (
                                                    <Box key={rowKey} sx={{ display: "grid", gridTemplateColumns: `${uploadRowIconColumn} ${uploadRowNameColumn} ${uploadRowDescriptionColumn} ${uploadRowNameColumn} ${uploadRowCategoryColumn} ${uploadRowStatusColumn} ${uploadRowDeleteColumn}`, gap: 1.25, px: 1.5, py: 1.25, alignItems: "center", borderBottom: "1px solid #eef2f6", backgroundColor: "#fff" }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                                            <Box sx={{ width: 34, height: 34, borderRadius: 1, ...rowTypeColors, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: getFileTypeBadgeFontSize(rowTypeLabel), lineHeight: 1, letterSpacing: 0.2, flex: "0 0 auto", textTransform: "uppercase", px: 0.35 }}>
                                                                {rowTypeLabel}
                                                            </Box>
                                                        </Box>
                                                        <TextField size="small" fullWidth sx={urlRowFieldSx} value={rowName} placeholder="Document name" onChange={(e) => {
                                                            updateUrlDocumentRow(rowKey, { name: e.target.value });
                                                            if (e.target.value && e.target.value.trim() !== "") {
                                                                    setValidationErrors((prev) => {
                                                                        const next = { ...prev };
                                                                    delete next[rowKey];
                                                                            delete next[`${rowKey}_name`];
                                                                        return next;
                                                                    });
                                                            }
                                                        }} error={!!validationErrors[rowKey] || !!validationErrors[`${rowKey}_name`]} helperText={validationErrors[rowKey] || validationErrors[`${rowKey}_name`] || ""} />
                                                        <TextField size="small" fullWidth sx={urlRowFieldSx} value={rowDescription} placeholder="Description" onChange={(e) => updateUrlDocumentRow(rowKey, { description: e.target.value })} />
                                                        <TextField size="small" fullWidth sx={urlRowFieldSx} value={rowUrl} placeholder="Document URL" onChange={(e) => {
                                                            updateUrlDocumentRow(rowKey, { url: e.target.value });
                                                            if (e.target.value && e.target.value.trim() !== "") {
                                                                    setValidationErrors((prev) => {
                                                                        const next = { ...prev };
                                                                            delete next[`${rowKey}_url`];
                                                                            return next;
                                                                        });
                                                                    }
                                                        }} error={!!validationErrors[`${rowKey}_url`]} helperText={validationErrors[`${rowKey}_url`] || ""} />
                                                        <Autocomplete multiple freeSolo size="small" options={categorySelectOptions} value={rowCategories} onChange={(event, newValue) => { const nextCategories = normalizeCategorySelection(newValue); updateBulkRowCategorySelection(rowKey, nextCategories, urlDocumentRows.map((item) => item.key)); if (newValue && newValue.length > 0) { setValidationErrors((prev) => { const next = { ...prev }; delete next[`${rowKey}_category`]; return next; }); } }} getOptionLabel={(option) => option.label || ""} isOptionEqualToValue={isSameCategoryOption} sx={bulkMultiSelectSx} renderTags={(value, getTagProps) => renderScrollableTags(value, getTagProps, (option) => option.label || "", 44)} renderInput={(params) => (<TextField {...params} sx={urlRowFieldSx} label={<RequiredLabel>Category</RequiredLabel>} placeholder={rowCategories.length > 0 ? "" : "Category"} error={!!validationErrors[`${rowKey}_category`]} helperText={validationErrors[`${rowKey}_category`] || ""} />)} />
                                                        <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
                                                            <InputLabel>Status</InputLabel>
                                                            <Select label="Status" value={rowDetails.status || newDoc.status || "private"} onChange={(e) => updateBulkFileDetail(rowKey, { status: e.target.value })}>
                                                                <MenuItem value="private">Private</MenuItem>
                                                                <MenuItem value="public">Public</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                        <IconButton aria-label={`Remove ${rowName || rowUrl || "url document"}`} color="error" onClick={() => removeUrlDocumentRow(rowKey)} size="small">
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            {newDoc.attachment_type === "url" && !isEditing && uploadTabIndex === 1 && (
                                <Box sx={{ mt: 2, border: "1px solid #e9edf2", borderRadius: 1.5, overflow: "hidden" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, px: 2, py: 1.5, backgroundColor: "#f8f9fa", borderBottom: "1px solid #e9edf2" }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Apply to all
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                            Assign machines for all URL documents
                                        </Typography>
                                    </Box>
                                    <Box sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                            <RequiredLabel>Machines</RequiredLabel>
                                        </Typography>
                                        <Autocomplete
                                            size="small"
                                            multiple
                                            options={machines}
                                            disablePortal
                                            filterOptions={(x) => x}
                                            getOptionLabel={(machine) => `${machine.machine_number} - ${machine.machine_name}`}
                                            isOptionEqualToValue={(option, value) => option.machine_id === value.machine_id}
                                            value={selectedMachines}
                                            onChange={(event, newValue) => {
                                                const nextMachines = newValue || [];
                                                const previousMachines = selectedMachines || [];
                                                const previousMachineIds = new Set(previousMachines.map((machine) => machine?.machine_id).filter(Boolean));
                                                const nextMachineIds = new Set(nextMachines.map((machine) => machine?.machine_id).filter(Boolean));
                                                const addedMachines = nextMachines.filter((machine) => machine?.machine_id && !previousMachineIds.has(machine.machine_id));
                                                const removedMachines = previousMachines.filter((machine) => machine?.machine_id && !nextMachineIds.has(machine.machine_id));
                                                setSelectedMachines(nextMachines);
                                                setBulkFileDetails((prev) => {
                                                    const next = { ...prev };
                                                    urlDocumentRows.forEach((row) => {
                                                        const key = row.key;
                                                        const existingMachines = Array.isArray(next[key]?.machines) ? next[key].machines : [];
                                                        const mergedMachines = mergeMachineSelections(existingMachines, addedMachines);
                                                        next[key] = {
                                                            ...(next[key] || {}),
                                                            machines: removeMachinesFromSelection(mergedMachines, removedMachines),
                                                        };
                                                    });
                                                    return next;
                                                });
                                            }}
                                            onInputChange={(e, value) => {
                                                setMachineSearch(value);
                                                clearTimeout(window.__md_search_timer__);
                                                window.__md_search_timer__ = setTimeout(() => {
                                                    fetchMachineResults(1, value || "");
                                                }, 300);
                                            }}
                                            sx={bulkMultiSelectSx}
                                            renderTags={(value, getTagProps) =>
                                                renderScrollableTags(value, getTagProps, (machine) => `${machine.machine_number} - ${machine.machine_name}`)
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    placeholder={selectedMachines.length > 0 ? "" : "Select machines..."}
                                                    error={!!validationErrors.machine}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {machinesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    </Box>
                                    <Box sx={{ borderTop: "1px solid #e9edf2" }}>
                                        <Box sx={{ display: "grid", gridTemplateColumns: `${uploadRowIconColumn} ${uploadRowNameColumn} ${uploadRowNameColumn} ${uploadRowCategoryColumn} ${uploadRowStatusColumn} ${uploadRowMachineColumn}`, gap: 1.25, px: 1.5, py: 1, backgroundColor: "#f7f9fb", fontWeight: 700, alignItems: "center" }}>
                                            <span />
                                            <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Name</RequiredLabel></Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Document URL</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Category</RequiredLabel></Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Status</Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Machine</RequiredLabel></Typography>
                                        </Box>
                                        {urlDocumentRows.map((row, rowIndex) => {
                                            const rowKey = row.key;
                                            const rowDetails = bulkFileDetails[rowKey] || {};
                                                          const rowCategories = getAppliedRowCategories(rowDetails);
                                            const rowMachines = rowDetails.machines || selectedMachines;
                                            const rowName = getRowNameValue(rowDetails, row.name || "");
                                            const rowUrl = String(rowDetails.url || row.url || "").trim();
                                                          const rowTypeLabel = "URL";
                                                          const rowTypeColors = { backgroundColor: "#e6f3ff", color: "#1976d2" };
                                            const categoryLabel = (rowCategories || []).map((category) => category?.label || category?.category_name || "").filter(Boolean).join(", ") || "Uncategorized";
                                            const statusLabel = (rowDetails.status || newDoc.status || "private") === "public" ? "Public" : "Private";
                                            return (
                                                <Box key={`${rowKey}-url-machine-review`} sx={{ display: "grid", gridTemplateColumns: `${uploadRowIconColumn} ${uploadRowNameColumn} ${uploadRowNameColumn} ${uploadRowCategoryColumn} ${uploadRowStatusColumn} ${uploadRowMachineColumn}`, gap: 1.25, px: 1.5, py: 1.25, alignItems: "center", borderBottom: "1px solid #eef2f6", backgroundColor: "#fff" }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                                        <Box sx={{ width: 34, height: 34, borderRadius: 1, ...rowTypeColors, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: getFileTypeBadgeFontSize(rowTypeLabel), lineHeight: 1, letterSpacing: 0.2, flex: "0 0 auto", textTransform: "uppercase", px: 0.35 }}>
                                                            {rowTypeLabel}
                                                        </Box>
                                                    </Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rowName || "Untitled"}</Typography>
                                                    <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rowUrl || "No URL"}</Typography>
                                                    <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{categoryLabel}</Typography>
                                                    <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{statusLabel}</Typography>
                                                    <Autocomplete
                                                        multiple
                                                        size="small"
                                                        options={machines}
                                                        disablePortal
                                                        slotProps={getMachineAutocompleteSlotProps(rowIndex, urlDocumentRows.length)}
                                                        filterOptions={(x) => x}
                                                        getOptionLabel={(machine) => `${machine.machine_number} - ${machine.machine_name}`}
                                                        isOptionEqualToValue={(option, value) => option.machine_id === value.machine_id}
                                                        value={rowMachines}
                                                        onChange={(event, newValue) => updateBulkRowMachineSelection(rowKey, newValue || [], urlDocumentRows.map((item) => item.key))}
                                                        onInputChange={(e, value) => {
                                                            setMachineSearch(value);
                                                            clearTimeout(window.__md_search_timer__);
                                                            window.__md_search_timer__ = setTimeout(() => {
                                                                fetchMachineResults(1, value || "");
                                                            }, 300);
                                                        }}
                                                        sx={bulkMultiSelectSx}
                                                        renderTags={(value, getTagProps) => renderScrollableTags(value, getTagProps, (machine) => `${machine.machine_number} - ${machine.machine_name}`, 36)}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                fullWidth
                                                                placeholder={rowMachines.length > 0 ? "" : "Select machines..."}
                                                                error={!!validationErrors[`${rowKey}_machine`]}
                                                                helperText={validationErrors[`${rowKey}_machine`] || ""}
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    endAdornment: (
                                                                        <>
                                                                            {machinesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                                            {params.InputProps.endAdornment}
                                                                        </>
                                                                    ),
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            )}

                            {(newDoc.attachment_type === 'file' || newDoc.attachment_type === 'url') && (
                                <Box>
                                    {!uploadTabIndex && isEditing && newDoc.attachment_type === 'file' && (
                                        <Box sx={{ mb: 1.5 }}>
                                            <div style={{ fontSize: 12, marginBottom: 6, color: "#666" }}>Current Attachment:</div>
                                            {(() => {
                                                const current = editingDocument || documents.find((d) => d.id === editDocId) || {};
                                                const link = getDocumentFileUrl(current.attachment, current.name);
                                                const typeLabel = (() => {
                                                    const n = current.name || "";
                                                    const idx = n.lastIndexOf(".");
                                                    return idx > -1 ? n.slice(idx + 1).toUpperCase() : "FILE";
                                                })();
                                                return (
                                                    <DocCard
                                                        title={current.name || "Open current file"}
                                                        href={link}
                                                        chipLabel={typeLabel}
                                                        imageProps={{ filename: current.name, path: current.attachment }}
                                                    />
                                                );
                                            })()}
                                            <div style={{ fontSize: 12, marginTop: 8, color: "#666" }}>Select a new file below to replace (optional):</div>
                                        </Box>
                                    )}
                                    {!uploadTabIndex && isEditing && newDoc.attachment_type === 'file' && (
                                        <FileDropZone
                                            key={fileDropzoneKey}
                                            maxFiles={1}
                                            maxTotalSizeLabel="1 GB total"
                                            files={newDoc.files}
                                            showSelectedFiles
                                            setParentData={(updater) => {
                                                setNewDoc((prev) => {
                                                    const updated = typeof updater === "function" ? updater(prev) : updater;
                                                    const nextFiles = Array.isArray(updated.files) ? updated.files.slice(0, 1) : [];
                                                    const totalSize = nextFiles.reduce((total, file) => total + (Number(file?.size) || 0), 0);
                                                    if (totalSize > MAX_TOTAL_UPLOAD_SIZE) {
                                                        setError((prevErr) => ({
                                                            ...prevErr,
                                                            file: `Total selected files must not exceed ${formatFileSize(MAX_TOTAL_UPLOAD_SIZE)}`,
                                                        }));
                                                        setFileDropzoneKey((k) => k + 1);
                                                        return {
                                                            ...prev,
                                                            files: Array.isArray(prev.files) ? prev.files : [],
                                                        };
                                                    }
                                                    setError((prevErr) => ({
                                                        ...prevErr,
                                                        file: undefined,
                                                    }));
                                                    return {
                                                        ...prev,
                                                        files: nextFiles,
                                                    };
                                                });
                                            }}
                                        />
                                    )}
                                    {!uploadTabIndex && isEditing && newDoc.attachment_type === 'url' && (
                                        <Box sx={{ mt: 2, border: "1px solid #e9edf2", borderRadius: 1.5, overflow: "hidden" }}>
                                            <Box sx={{ display: "grid", gridTemplateColumns: `${uploadRowIconColumn} ${uploadRowNameColumn} ${uploadRowDescriptionColumn} ${uploadRowNameColumn} ${uploadRowCategoryColumn} ${uploadRowStatusColumn}`, gap: 1.25, px: 1.5, py: 1, backgroundColor: "#f7f9fb", borderBottom: "1px solid #e9edf2", fontWeight: 700, alignItems: "center" }}>
                                                <span />
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Name</RequiredLabel></Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Description</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Document URL</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Category</RequiredLabel></Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Status</Typography>
                                            </Box>
                                            {uploadRows.map((row) => {
                                                const rowKey = row.key || `edit-${editDocId || "document"}`;
                                                const rowDetails = {
                                                    name: newDoc.name ?? editingDocument?.name ?? "",
                                                    description: newDoc.description ?? editingDocument?.description ?? "",
                                                    url: newDoc.url ?? editingDocument?.attachment ?? "",
                                                    categories: selectedDocCategories,
                                                    status: newDoc.status,
                                                };
                                                const rowCategories = getAppliedRowCategories(rowDetails);
                                                const rowName = getRowNameValue(rowDetails, "");
                                                const rowDescription = getRowDescriptionValue(rowDetails, "");
                                                const rowUrl = String(rowDetails.url || "").trim();
                                                const rowTypeLabel = "URL";
                                                const rowTypeColors = { backgroundColor: "#e6f3ff", color: "#1976d2" };
                                                return (
                                                    <Box key={rowKey} sx={{ display: "grid", gridTemplateColumns: `${uploadRowIconColumn} ${uploadRowNameColumn} ${uploadRowDescriptionColumn} ${uploadRowNameColumn} ${uploadRowCategoryColumn} ${uploadRowStatusColumn}`, gap: 1.25, px: 1.5, py: 1.25, alignItems: "center", borderBottom: "1px solid #eef2f6", backgroundColor: "#fff" }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                                            <Box sx={{ width: 34, height: 34, borderRadius: 1, ...rowTypeColors, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: getFileTypeBadgeFontSize(rowTypeLabel), lineHeight: 1, letterSpacing: 0.2, flex: "0 0 auto", textTransform: "uppercase", px: 0.35 }}>
                                                                {rowTypeLabel}
                                                            </Box>
                                                        </Box>
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            value={rowName}
                                                            placeholder="Document name"
                                                            onChange={(e) => {
                                                                setNewDoc((prev) => ({ ...prev, name: e.target.value }));
                                                                if (e.target.value && e.target.value.trim() !== "") {
                                                                    setValidationErrors((prev) => {
                                                                        const next = { ...prev };
                                                                        delete next[`${rowKey}_name`];
                                                                        return next;
                                                                    });
                                                                }
                                                            }}
                                                            error={!!validationErrors[`${rowKey}_name`]}
                                                            helperText={validationErrors[`${rowKey}_name`] || ""}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            value={rowDescription}
                                                            placeholder="Description"
                                                            onChange={(e) => setNewDoc((prev) => ({ ...prev, description: e.target.value }))}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            value={rowUrl}
                                                            placeholder="Document URL"
                                                            onChange={(e) => {
                                                                setNewDoc((prev) => ({ ...prev, url: e.target.value }));
                                                                if (e.target.value && e.target.value.trim() !== "") {
                                                                    setValidationErrors((prev) => {
                                                                        const next = { ...prev };
                                                                        delete next[`${rowKey}_url`];
                                                                        return next;
                                                                    });
                                                                }
                                                            }}
                                                            error={!!validationErrors[`${rowKey}_url`]}
                                                            helperText={validationErrors[`${rowKey}_url`] || ""}
                                                        />
                                                        <Autocomplete
                                                            multiple
                                                            freeSolo
                                                            size="small"
                                                            options={categorySelectOptions}
                                                            value={rowCategories}
                                                            onChange={(event, newValue) => {
                                                                handleCategorySelection(event, newValue);
                                                                setValidationErrors((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next[`${rowKey}_category`];
                                                                    return next;
                                                                });
                                                            }}
                                                            getOptionLabel={(option) => option.label || ""}
                                                            isOptionEqualToValue={isSameCategoryOption}
                                                            sx={bulkMultiSelectSx}
                                                            renderTags={(value, getTagProps) =>
                                                                renderScrollableTags(value, getTagProps, (option) => option.label || "", 44)
                                                            }
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    placeholder={rowCategories.length > 0 ? "" : "Category"}
                                                                    error={!!validationErrors[`${rowKey}_category`]}
                                                                    helperText={validationErrors[`${rowKey}_category`] || ""}
                                                                />
                                                            )}
                                                        />
                                                        <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
                                                            <Select
                                                                label="Status"
                                                                value={rowDetails.status || "private"}
                                                                onChange={(e) => setNewDoc((prev) => ({ ...prev, status: e.target.value }))}
                                                            >
                                                                <MenuItem value="private">Private</MenuItem>
                                                                <MenuItem value="public">Public</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}
                                    {!uploadTabIndex && !isEditing && newDoc.attachment_type === 'file' && (
                                        <FileDropZone
                                            key={fileDropzoneKey}
                                            maxFiles={0}
                                            maxTotalSizeLabel="1 GB total"
                                            files={selectedDocumentFiles}
                                            showSelectedFiles={false}
                                            setParentData={(updater) => {
                                                setNewDoc((prev) => {
                                                    const updated =
                                                        typeof updater === "function" ? updater(prev) : updater;

                                                    const totalSize = (updated.files || []).reduce((total, file) => total + (Number(file?.size) || 0), 0);
                                                    if (totalSize > MAX_TOTAL_UPLOAD_SIZE) {
                                                        setError((prevErr) => ({
                                                            ...prevErr,
                                                            file: `Total selected files must not exceed ${formatFileSize(MAX_TOTAL_UPLOAD_SIZE)}`,
                                                        }));
                                                        setFileDropzoneKey((k) => k + 1);
                                                        return {
                                                            ...prev,
                                                            files: Array.isArray(prev.files) ? prev.files : [],
                                                        };
                                                    }
                                                    setError((prevErr) => ({
                                                        ...prevErr,
                                                        file: undefined,
                                                    }));
                                                    return {
                                                        ...prev,
                                                        files: updated.files,
                                                    };
                                                });
                                            }}
                                        />
                                    )}

                                    {!uploadTabIndex && isEditing && newDoc.attachment_type === 'file' && (
                                        <Box sx={{ mt: 2, border: "1px solid #e9edf2", borderRadius: 1.5, overflow: "hidden" }}>
                                            <Box
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: `${uploadRowIconColumn} ${uploadRowNameColumn} ${uploadRowDescriptionColumn} ${uploadRowCategoryColumn} ${uploadRowStatusColumn}`,
                                                    gap: 1.25,
                                                    px: 1.5,
                                                    py: 1,
                                                    backgroundColor: "#f7f9fb",
                                                    borderBottom: "1px solid #e9edf2",
                                                    fontWeight: 700,
                                                    alignItems: "center",
                                                }}
                                            >
                                                <span />
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Name</RequiredLabel></Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Description</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Category</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Status</Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: `${uploadRowIconColumn} ${uploadRowNameColumn} ${uploadRowDescriptionColumn} ${uploadRowCategoryColumn} ${uploadRowStatusColumn}`,
                                                    gap: 1.25,
                                                    px: 1.5,
                                                    py: 1.25,
                                                    alignItems: "center",
                                                    borderBottom: "1px solid #eef2f6",
                                                    backgroundColor: "#fff",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                                    <Box
                                                        sx={{
                                                            width: 34,
                                                            height: 34,
                                                            borderRadius: 1,
                                                            ...getFileTypeBadgeColors(newDoc.name ?? editingDocument?.name ?? ""),
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontWeight: 700,
                                                            fontSize: getFileTypeBadgeFontSize(getFileTypeBadgeLabel(newDoc.name ?? editingDocument?.name ?? "")),
                                                            lineHeight: 1,
                                                            letterSpacing: 0.2,
                                                            flex: "0 0 auto",
                                                            textTransform: "uppercase",
                                                            px: 0.35,
                                                        }}
                                                    >
                                                        {getFileTypeBadgeLabel(newDoc.name ?? editingDocument?.name ?? "")}
                                                    </Box>
                                                </Box>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    value={newDoc.name ?? editingDocument?.name ?? ""}
                                                    placeholder="Document name"
                                                    onChange={(e) => setNewDoc((prev) => ({ ...prev, name: e.target.value }))}
                                                    error={!!error.name}
                                                    helperText={error.name}
                                                />
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    value={newDoc.description ?? editingDocument?.description ?? ""}
                                                    placeholder="Description"
                                                    onChange={(e) => setNewDoc((prev) => ({ ...prev, description: e.target.value }))}
                                                />
                                                <Autocomplete
                                                    multiple
                                                    freeSolo
                                                    size="small"
                                                    options={categorySelectOptions}
                                                    value={selectedDocCategories}
                                                    onChange={handleCategorySelection}
                                                    getOptionLabel={(option) => option.label || ""}
                                                    isOptionEqualToValue={isSameCategoryOption}
                                                    sx={bulkMultiSelectSx}
                                                    renderTags={(value, getTagProps) =>
                                                        renderScrollableTags(value, getTagProps, (option) => option.label || "", 44)
                                                    }
                                                    renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label={<RequiredLabel>Category</RequiredLabel>}
                                                        placeholder={selectedDocCategories.length > 0 ? "" : "Category"}
                                                    />
                                                )}
                                            />
                                                <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
                                                    <InputLabel>Status</InputLabel>
                                                    <Select
                                                        label="Status"
                                                        value={newDoc.status || "private"}
                                                        onChange={(e) => setNewDoc((prev) => ({ ...prev, status: e.target.value }))}
                                                    >
                                                        <MenuItem value="private">Private</MenuItem>
                                                        <MenuItem value="public">Public</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Box>
                                    )}

                                    {/* {!uploadTabIndex && !isEditing && newDoc.attachment_type === "file" && hasUploadRows && (
                                        <Box
                                            sx={{
                                                mt: 1.5,
                                                p: 1.5,
                                                border: "1px solid #e6eef5",
                                                borderRadius: 1.5,
                                                backgroundColor: "#fbfdff",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    Upload Batch Summary
                                                </Typography>
                                            </Box>
                                            {isBulkDocumentUpload && (
                                                <Alert severity="info" sx={{ mt: 1.25 }}>
                                                    Review name, category, status, and delete actions for each uploaded file before adding documents.
                                                </Alert>
                                            )}
                                            
                                                                                    </Box>
                                    )} */}

                                    {(showFileUploadStepOneTable || (uploadTabIndex === 1 && (newDoc.attachment_type === "file" || isEditing))) && (
    <Box ref={selectedFilesTableRef} sx={{ mt: 2.5 }}>
                                            {/* Page 1: Document Details */}
                                            {/* {!uploadTabIndex && (
                                                <Box>
                                                    <Box sx={{ overflowX: "hidden", border: "1px solid #e9edf2", borderRadius: 1.5 }}> */}
                                            {!uploadTabIndex && showFileUploadStepOneTable && (
                                                <Box>
                                                    {isBulkDocumentUpload && (
                                                        <Box sx={{ mb: 2, p: 1.5, border: "1px solid #e9edf2", borderRadius: 1.5, backgroundColor: "#f8f9fa" }}>
                                                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                                                                Apply to all documents:
                                                            </Typography>
                                                            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                                                                <Box sx={{ minWidth: 200 }}>
                                                                    <Autocomplete
                                                                        multiple
                                                                        freeSolo
                                                                        size="small"
                                                                        options={categorySelectOptions}
                                                    value={selectedDocCategories}
                                                                        onChange={handleCategorySelection}
                                                    getOptionLabel={(option) => option.label || ""}
                                                    isOptionEqualToValue={isSameCategoryOption}
                                                    sx={bulkMultiSelectSx}
                                                                        renderTags={(value, getTagProps) =>
                                                                            renderScrollableTags(value, getTagProps, (option) => option.label || "", 44)
                                                                        }
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                label="Category"
                                                                                placeholder={selectedDocCategories.length > 0 ? "" : "Category"}
                                                                            />
                                                                        )}
                                                                    />
                                                                </Box>
                                                                <Box sx={{ minWidth: 150 }}>
                                                                    <FormControl size="small" fullWidth>
                                                                        <InputLabel>Status</InputLabel>
                                                                        <Select
                                                                            label="Status"
                                                                            value={newDoc.status || "private"}
                                                                            onChange={(e) => {
                                                                                const nextStatus = e.target.value;
                                                                                setNewDoc((prev) => ({ ...prev, status: nextStatus }));
                                                                                setBulkFileDetails((prev) => {
                                                                                    const next = { ...prev };
                                                                                    selectedDocumentFiles.forEach((file) => {
                                                                                        const fileKey = getBulkFileKey(file);
                                                                                        next[fileKey] = {
                                                                                            ...(next[fileKey] || {}),
                                                                                            status: nextStatus,
                                                                                        };
                                                                                    });
                                                                                    return next;
                                                                                });
                                                                            }}
                                                                        >
                                                                            <MenuItem value="private">Private</MenuItem>
                                                                            <MenuItem value="public">Public</MenuItem>
                                                                        </Select>
                                                                    </FormControl>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    )}
                                                    <Box sx={{ overflowX: "hidden", border: "1px solid #e9edf2", borderRadius: 1.5 }}>
                                                        <Box sx={{ width: "100%", minWidth: 0 }}>
                                                            <Box
                                                                sx={{
                                                                    display: "grid",
                                                                    gridTemplateColumns: uploadStepOneColumns,
                                                                    gap: 1.25,
                                                                    px: 1.5,
                                                                    py: 1,
                                                                    backgroundColor: "#f7f9fb",
                                                                    borderBottom: "1px solid #e9edf2",
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                <span />
                                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Name</RequiredLabel></Typography>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Description</Typography>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Category</Typography>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Status</Typography>
                                                                <span />
                                                            </Box>

                                                            {uploadRows.map((file) => {
                                                                const isEditRow = Boolean(file.isEditRow);
                                                                const fileKey = file.key || getBulkFileKey(file);
                                                                const fileDetails = isEditRow
                                                                    ? {
                                                                        name: newDoc.name,
                                                                        description: newDoc.description,
                                                                        categories: selectedDocCategories,
                                                                        status: newDoc.status,
                                                                    }
                                                                    : bulkFileDetails[fileKey] || {};
                                                                 const fileCategories = isEditRow ? selectedDocCategories : getAppliedRowCategories(fileDetails);
                                                                 const fileName = isEditRow ? (newDoc.name ?? editingDocument?.name ?? "") : file.name;
                                                                const fileTypeLabel = getFileTypeBadgeLabel(fileName);
                                                                const fileTypeColors = getFileTypeBadgeColors(file.name);
                                                                return (
                                                                    <Box
                                                                        key={fileKey}
                                                                        sx={{
                                                                            display: "grid",
                                                                            gridTemplateColumns: uploadStepOneColumns,
                                                                            gap: 1.25,
                                                                            px: 1.5,
                                                                            py: 1.25,
                                                                            alignItems: "center",
                                                                            borderBottom: "1px solid #eef2f6",
                                                                            backgroundColor: "#fff",
                                                                        }}
                                                                    >
                                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                                                            <Box
                                                                                sx={{
                                                                                    width: 34,
                                                                                    height: 34,
                                                                                    borderRadius: 1,
                                                                                    ...fileTypeColors,
                                                                                    display: "flex",
                                                                                    alignItems: "center",
                                                                                    justifyContent: "center",
                                                                                    fontWeight: 700,
                                                                                    fontSize: getFileTypeBadgeFontSize(fileTypeLabel),
                                                                                    lineHeight: 1,
                                                                                    letterSpacing: 0.2,
                                                                                    flex: "0 0 auto",
                                                                                    textTransform: "uppercase",
                                                                                    px: 0.35,
                                                                                }}
                                                                            >
                                                                                {fileTypeLabel}
                                                                            </Box>
                                                                        </Box>
                                                                        <Box sx={{ position: 'relative', minWidth: 0, width: "100%" }}>
                                                                            <TextField
                                                                                size="small"
                                                                                fullWidth
                                                                               value={isEditRow ? (newDoc.name ?? editingDocument?.name ?? "") : getRowNameValue(fileDetails, getFileBaseName(file.name))}
                                                                                placeholder="Document name"
                                                                                onChange={(e) => {
                                                                                    if (isEditRow) {
                                                                                        setNewDoc((prev) => ({ ...prev, name: e.target.value }));
                                                                                    } else {
                                                                                        updateBulkFileDetail(fileKey, { name: e.target.value });
                                                                                    }
                                                                                    if (e.target.value && e.target.value.trim() !== "") {
                                                                                        setValidationErrors((prev) => {
                                                                                            const newErrors = { ...prev };
                                                                                            delete newErrors[fileKey];
                                                                                            delete newErrors.name;
                                                                                            return newErrors;
                                                                                        });
                                                                                    }
                                                                                }}
                                                                                error={!!validationErrors[fileKey]}
                                                                            />
                                                                            {validationErrors[fileKey] && (
                                                                                <Typography 
                                                                                    variant="caption" 
                                                                                    color="error" 
                                                                                    sx={{ 
                                                                                        position: 'absolute',
                                                                                        top: '100%',
                                                                                        left: 0,
                                                                                        mt: 0.5,
                                                                                        whiteSpace: 'nowrap',
                                                                                        zIndex: 10
                                                                                    }}
                                                                                >
                                                                                    {validationErrors[fileKey]}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                        <Box sx={{ minWidth: 0, width: "100%" }}>
                                                                            <TextField
                                                                                size="small"
                                                                                fullWidth
                                                                                value={isEditRow ? (newDoc.description ?? editingDocument?.description ?? "") : getRowDescriptionValue(fileDetails, "")}
                                                                                placeholder="Description"
                                                                                onChange={(e) => {
                                                                                    if (isEditRow) {
                                                                                        setNewDoc((prev) => ({ ...prev, description: e.target.value }));
                                                                                    } else {
                                                                                        updateBulkFileDetail(fileKey, { description: e.target.value });
                                                                                    }
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                        <Box sx={{ position: 'relative', minWidth: 0, width: "100%" }}>
                                                                <Autocomplete
                                                                                multiple
                                                                                freeSolo
                                                                                size="small"
                                                                                options={categorySelectOptions}
                                                                                value={fileCategories}
                                                                                 onChange={(event, newValue) => {
                                                                                      const nextCategories = normalizeCategorySelection(newValue);
                                                                                      if (isEditRow) {
                                                                                          setSelectedDocCategories(nextCategories);
                                                                                      } else {
                                                                                       updateBulkRowCategorySelection(fileKey, nextCategories, selectedDocumentFiles.map(getBulkFileKey));
                                                                                      }
                                                                                    if (newValue && newValue.length > 0) {
                                                                                        setValidationErrors((prev) => {
                                                                                            const newErrors = { ...prev };
                                                                                            delete newErrors.categories;
                                                                                            delete newErrors[`${fileKey}_category`];
                                                                                            return newErrors;
                                                                                        });
                                                                                    }
                                                                                }}
                                                                                getOptionLabel={(option) => option.label || ""}
                                                                                isOptionEqualToValue={isSameCategoryOption}
                                                                                sx={bulkMultiSelectSx}
                                                                                renderTags={(value, getTagProps) =>
                                                                                    renderScrollableTags(value, getTagProps, (option) => option.label || "", 44)
                                                                                }
                                                                                renderInput={(params) => (
                                                                                                <TextField
                                                                                                    {...params}
                                                                                                    fullWidth
                                                                                                     placeholder={fileCategories.length > 0 ? "" : "Category"}
                                                                                                    error={!!validationErrors[`${fileKey}_category`]}
                                                                                                />
                                                                                )}
                                                                            />
                                                                            {validationErrors[`${fileKey}_category`] && (
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    color="error"
                                                                                    sx={{
                                                                                        position: 'absolute',
                                                                                        top: '100%',
                                                                                        left: 0,
                                                                                        mt: 0.5,
                                                                                        whiteSpace: 'nowrap',
                                                                                        zIndex: 10
                                                                                    }}
                                                                                >
                                                                                    {validationErrors[`${fileKey}_category`]}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                        <FormControl size="small" fullWidth sx={{ minWidth: 0 }}>
                                                                            <Select
                                                                                value={isEditRow ? (newDoc.status || "private") : (fileDetails.status || newDoc.status || "private")}
                                                                                onChange={(e) => {
                                                                                    if (isEditRow) {
                                                                                        setNewDoc((prev) => ({ ...prev, status: e.target.value }));
                                                                                    } else {
                                                                                        updateBulkFileDetail(fileKey, { status: e.target.value });
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <MenuItem value="private">Private</MenuItem>
                                                                                <MenuItem value="public">Public</MenuItem>
                                                                            </Select>
                                                                        </FormControl>
                                                                        {!isEditRow ? (
                                                                            <IconButton aria-label={`Remove ${file.name}`} color="error" onClick={() => removeSelectedDocumentFile(file)} size="small">
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        ) : (
                                                                            <span />
                                                                        )}
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>
                                                    </Box>
                                                                                                                                                        </Box>
                                            )}

                                            {/* Page 2: Machine Mapping */}
                                            {uploadTabIndex === 1 && !isEditing && newDoc.attachment_type === "file" && (
                                                <Box>
                                                    {!isEditing && (
                                                        <>
                                                            {/* Apply to all section */}
                                                            <Box sx={{ mb: 3, p: 2, border: "1px solid #e9edf2", borderRadius: 1.5, backgroundColor: "#f8f9fa" }}>
                                                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                                                    Apply to all
                                                                </Typography>
                                                                {/* Machine Assignment */}
                                                                <Box sx={{ mb: 2 }}>
                                                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                                                        <RequiredLabel>Machines</RequiredLabel>
                                                                    </Typography>
                                                                    <Autocomplete
                                                                size="small"
                                                                multiple
                                                                options={machines}
                                                                disablePortal
                                                                filterOptions={(x) => x}
                                                                getOptionLabel={(machine) => `${machine.machine_number} - ${machine.machine_name}`}
                                                                isOptionEqualToValue={(option, value) => option.machine_id === value.machine_id}
                                                                value={selectedMachines}
                                                                onChange={(event, newValue) => {
                                                                    const nextMachines = newValue || [];
                                                                    const previousMachines = selectedMachines || [];
                                                                    const previousMachineIds = new Set(previousMachines.map((machine) => machine?.machine_id).filter(Boolean));
                                                                    const nextMachineIds = new Set(nextMachines.map((machine) => machine?.machine_id).filter(Boolean));
                                                                    const addedMachines = nextMachines.filter((machine) => machine?.machine_id && !previousMachineIds.has(machine.machine_id));
                                                                    const removedMachines = previousMachines.filter((machine) => machine?.machine_id && !nextMachineIds.has(machine.machine_id));
                                                                    setSelectedMachines(nextMachines);
                                                                    setBulkFileDetails((prev) => {
                                                                        const next = { ...prev };
                                                                        selectedDocumentFiles.forEach((file) => {
                                                                            const key = getBulkFileKey(file);
                                                                            const existingMachines = Array.isArray(next[key]?.machines) ? next[key].machines : [];
                                                                            const mergedMachines = mergeMachineSelections(existingMachines, addedMachines);
                                                                            next[key] = {
                                                                                ...(next[key] || {}),
                                                                                machines: removeMachinesFromSelection(mergedMachines, removedMachines),
                                                                            };
                                                                        });
                                                                        return next;
                                                                    });
                                                                    setValidationErrors((prev) => {
                                                                        const next = { ...prev };
                                                                        selectedDocumentFiles.forEach((file) => {
                                                                            delete next[`${getBulkFileKey(file)}_machine`];
                                                                        });
                                                                        return next;
                                                                    });
                                                                }}
                                                                                            onInputChange={(e, value) => {
                                                                                                setMachineSearch(value);
                                                                                                clearTimeout(window.__md_search_timer__);
                                                                                                window.__md_search_timer__ = setTimeout(() => {
                                                                                                    fetchMachineResults(1, value || "");
                                                                                                }, 300);
                                                                                                }}
                                                                                            sx={bulkMultiSelectSx}
                                                                                            renderTags={(value, getTagProps) =>
                                                                                                renderScrollableTags(
                                                                                                    value,
                                                                                                    getTagProps,
                                                                                                    (machine) => `${machine.machine_number} - ${machine.machine_name}`
                                                                                                )
                                                                                            }
                                                                                            renderInput={(params) => (
                                                                                                <TextField
                                                                                                    {...params}
                                                                                             placeholder={selectedMachines.length > 0 ? "" : "Select machines..."}
                                                                          error={!!validationErrors.machine}
                                                                         InputProps={{
                                                                             ...params.InputProps,
                                                                             endAdornment: (
                                                                                <>
                                                                                    {machinesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                                                    {params.InputProps.endAdornment}
                                                                                </>
                                                                            ),
                                                                        }}
                                                                    />
                                                                )}
                                                                ListboxProps={{
                                                                    onScroll: (e) => {
                                                                        const listboxNode = e.currentTarget;
                                                                        if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10) {
                                                                            if (!machinesLoading && machineHasMore) {
                                                                                fetchMachineResults(machinePage + 1, machineSearch);
                                                                            }
                                                                        }
                                                                    },
                                                                    style: { maxHeight: 300, overflow: 'auto' },
                                                                }}
                                                            />
                                                                </Box>
                                                            </Box>
                                                        </>
                                                    )}
                                                    <Box sx={{ overflowX: "hidden", border: "1px solid #e9edf2", borderRadius: 1.5 }}>
                                                        <Box sx={{ width: "100%", minWidth: 0 }}>
                                                            <Box
                                                                sx={{
                                                                    display: "grid",
                                                                    gridTemplateColumns: uploadStepTwoColumns,
                                                                    gap: 1.25,
                                                                    px: 1.5,
                                                                    py: 1,
                                                                    backgroundColor: "#f7f9fb",
                                                                    borderBottom: "1px solid #e9edf2",
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                <span />
                                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Name</RequiredLabel></Typography>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Category</RequiredLabel></Typography>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Status</Typography>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Machine</RequiredLabel></Typography>
                                                            </Box>
                                                             {uploadRows.map((file, rowIndex) => {
                                                                  const isEditRow = Boolean(file.isEditRow);
                                                                  const fileKey = file.key || getBulkFileKey(file);
                                                                  const fileDetails = isEditRow
                                                                      ? {
                                                                          name: newDoc.name,
                                                                          categories: selectedDocCategories,
                                                                          machines: selectedMachines,
                                                                          status: newDoc.status,
                                                                      }
                                                                      : bulkFileDetails[fileKey] || {};
                                                                   const fileCategories = isEditRow ? selectedDocCategories : getAppliedRowCategories(fileDetails);
                                                                  const fileMachines = isEditRow ? selectedMachines : fileDetails.machines || selectedMachines;
                                                                  const fileName = isEditRow ? (newDoc.name ?? editingDocument?.name ?? "") : file.name;
                                                                  const fileTypeLabel = getFileTypeBadgeLabel(fileName);
                                                                  const fileTypeColors = getFileTypeBadgeColors(file.name);
                                                                 const categoryLabel = (fileCategories || [])
                                                                     .map((category) => category?.label || category?.category_name || "")
                                                                     .filter(Boolean)
                                                                     .join(", ") || "Uncategorized";
                                                                 const statusLabel = (fileDetails.status || newDoc.status || "private") === "public" ? "Public" : "Private";
                                                                 return (
                                                                    <Box
                                                                        key={`${fileKey}-machine-review`}
                                                                        sx={{
                                                                            display: "grid",
                                                                            gridTemplateColumns: uploadStepTwoColumns,
                                                                            gap: 1.25,
                                                                            px: 1.5,
                                                                            py: 1.25,
                                                                            alignItems: "center",
                                                                            borderBottom: "1px solid #eef2f6",
                                                                            backgroundColor: "#fff",
                                                                        }}
                                                                    >
                                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                                                            <Box
                                                                                sx={{
                                                                                    width: 34,
                                                                                    height: 34,
                                                                                    borderRadius: 1,
                                                                                    ...fileTypeColors,
                                                                                    display: "flex",
                                                                                    alignItems: "center",
                                                                                    justifyContent: "center",
                                                                                    fontWeight: 700,
                                                                                    fontSize: getFileTypeBadgeFontSize(fileTypeLabel),
                                                                                    lineHeight: 1,
                                                                                    letterSpacing: 0.2,
                                                                                    flex: "0 0 auto",
                                                                                    textTransform: "uppercase",
                                                                                    px: 0.35,
                                                                                }}
                                                                                >
                                                                                {fileTypeLabel}
                                                                            </Box>
                                                                        </Box>
                                                                        <Box sx={{ minWidth: 0 }}>
                                                                                <Typography variant="body2" sx={{ fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                                    {isEditRow ? (newDoc.name ?? editingDocument?.name ?? getFileBaseName(fileName)) : getRowNameValue(fileDetails, getFileBaseName(file.name))}
                                                                                </Typography>
                                                                            </Box>
                                                                        <Box sx={{ minWidth: 0 }}>
                                                                            <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                                {categoryLabel}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box sx={{ minWidth: 0 }}>
                                                                            <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                                {statusLabel}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box sx={{ minWidth: 0, width: "100%" }}>
                                                                         <Autocomplete
                                                                              multiple
                                                                              size="small"
                                                                              options={machines}
                                                                             disablePortal
                                                                             slotProps={getMachineAutocompleteSlotProps(rowIndex, uploadRows.length)}
                                                                             filterOptions={(x) => x}
                                                                             getOptionLabel={(machine) => `${machine.machine_number} - ${machine.machine_name}`}
                                                                             isOptionEqualToValue={(option, value) => option.machine_id === value.machine_id}
                                                                              value={fileMachines}
                                                                              onChange={(event, newValue) => {
                                                                                    const nextMachines = newValue || [];
                                                                                    if (isEditRow) {
                                                                                        setSelectedMachines(nextMachines);
                                                                                    } else {
                                                                                        updateBulkRowMachineSelection(fileKey, nextMachines, selectedDocumentFiles.map(getBulkFileKey));
                                                                                    }
                                                                                    setValidationErrors((prev) => {
                                                                                        const next = { ...prev };
                                                                                        delete next[`${fileKey}_machine`];
                                                                                        return next;
                                                                                    });
                                                                               }}
                                                                              onInputChange={(e, value) => {
                                                                                  setMachineSearch(value);
                                                                                  clearTimeout(window.__md_search_timer__);
                                                                                  window.__md_search_timer__ = setTimeout(() => {
                                                                                      fetchMachineResults(1, value || "");
                                                                                  }, 300);
                                                                               }}
                                                                               sx={bulkMultiSelectSx}
                                                                               renderTags={(value, getTagProps) =>
                                                                                   renderScrollableTags(
                                                                                       value,
                                                                                       getTagProps,
                                                                                       (machine) => `${machine.machine_number} - ${machine.machine_name}`,
                                                                                       36
                                                                                   )
                                                                               }
                                                                                renderInput={(params) => (
                                                                                                <TextField
                                                                                                    {...params}
                                                                                                    fullWidth
                                                                                       placeholder={fileMachines.length > 0 ? "" : "Select machines..."}
                                                                                       error={!!validationErrors[`${fileKey}_machine`]}
                                                                                       helperText={validationErrors[`${fileKey}_machine`] || ""}
                                                                                      InputProps={{
                                                                                         ...params.InputProps,
                                                                                         endAdornment: (
                                                                                             <>
                                                                                                 {machinesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                                                                 {params.InputProps.endAdornment}
                                                                                             </>
                                                                                         ),
                                                                                     }}
                                                                                 />
                                                                              )}
                                                                          />
                                                                        </Box>
                                                                      </Box>
                                                                  );
                                                              })}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            )}

                            {newDoc.attachment_type === "url" && uploadTabIndex === 1 && (
                                <Box sx={{ mt: 2, border: "1px solid #e9edf2", borderRadius: 1.5, overflow: "hidden" }}>
                                                    <Box sx={{ display: "grid", gridTemplateColumns: `${uploadRowIconColumn} ${uploadRowNameColumn} ${uploadRowNameColumn} ${uploadRowCategoryColumn} ${uploadRowStatusColumn} ${uploadRowMachineColumn}`, gap: 1.25, px: 1.5, py: 1, backgroundColor: "#f7f9fb", borderBottom: "1px solid #e9edf2", fontWeight: 700, alignItems: "center" }}>
                                                        <span />
                                                        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Name</RequiredLabel></Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Document URL</Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Category</RequiredLabel></Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Status</Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Machine</RequiredLabel></Typography>
                                                    </Box>
                                            {uploadRows.map((row, rowIndex) => {
                                                         const rowKey = row.key;
                                                         const rowDetails = isEditing
                                                             ? {
                                                                 name: newDoc.name ?? editingDocument?.name ?? "",
                                                                 url: newDoc.url ?? editingDocument?.attachment ?? "",
                                                                 categories: selectedDocCategories,
                                                                 machines: selectedMachines,
                                                                 status: newDoc.status,
                                                             }
                                                             : bulkFileDetails[rowKey] || {};
                                                          const rowCategories = getAppliedRowCategories(rowDetails);
                                                         const rowMachines = rowDetails.machines || selectedMachines;
                                                         const rowName = isEditing ? getRowNameValue(rowDetails, "") : getRowNameValue(rowDetails, row.name || "");
                                                         const rowUrl = String(isEditing ? (rowDetails.url || "") : (rowDetails.url || row.url || "")).trim();
                                                          const rowTypeLabel = "URL";
                                                          const rowTypeColors = { backgroundColor: "#e6f3ff", color: "#1976d2" };
                                                        const categoryLabel = (rowCategories || []).map((category) => category?.label || category?.category_name || "").filter(Boolean).join(", ") || "Uncategorized";
                                                        const statusLabel = (rowDetails.status || newDoc.status || "private") === "public" ? "Public" : "Private";
                                                        return (
                                                            <Box key={`${rowKey}-url-machine`} sx={{ display: "grid", gridTemplateColumns: `${uploadRowIconColumn} ${uploadRowNameColumn} ${uploadRowNameColumn} ${uploadRowCategoryColumn} ${uploadRowStatusColumn} ${uploadRowMachineColumn}`, gap: 1.25, px: 1.5, py: 1.25, alignItems: "center", borderBottom: "1px solid #eef2f6", backgroundColor: "#fff" }}>
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                                                    <Box sx={{ width: 34, height: 34, borderRadius: 1, ...rowTypeColors, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: getFileTypeBadgeFontSize(rowTypeLabel), lineHeight: 1, letterSpacing: 0.2, flex: "0 0 auto", textTransform: "uppercase", px: 0.35 }}>{rowTypeLabel}</Box>
                                                                </Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rowName || "Untitled"}</Typography>
                                                                <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rowUrl || "No URL"}</Typography>
                                                                <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{categoryLabel}</Typography>
                                                                <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{statusLabel}</Typography>
                                                                <Autocomplete
                                                                    multiple
                                                                    size="small"
                                                                    options={machines}
                                                                    disablePortal
                                                                    slotProps={getMachineAutocompleteSlotProps(rowIndex, uploadRows.length)}
                                                                    filterOptions={(x) => x}
                                                                    getOptionLabel={(machine) => `${machine.machine_number} - ${machine.machine_name}`}
                                                                    isOptionEqualToValue={(option, value) => option.machine_id === value.machine_id}
                                                                    value={rowMachines}
                                                                     onChange={(event, newValue) => {
                                                                         if (isEditing) {
                                                                             setSelectedMachines(newValue || []);
                                                                         } else {
                                                                              updateBulkRowMachineSelection(rowKey, newValue || [], uploadRows.map((item) => item.key || getBulkFileKey(item)));
                                                                         }
                                                                     }}
                                                                    onInputChange={(e, value) => {
                                                                        setMachineSearch(value);
                                                                        clearTimeout(window.__md_search_timer__);
                                                                        window.__md_search_timer__ = setTimeout(() => {
                                                                            fetchMachineResults(1, value || "");
                                                                        }, 300);
                                                                    }}
                                                                    sx={bulkMultiSelectSx}
                                                                    renderTags={(value, getTagProps) => renderScrollableTags(value, getTagProps, (machine) => `${machine.machine_number} - ${machine.machine_name}`, 36)}
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            fullWidth
                                                                            placeholder={rowMachines.length > 0 ? "" : "Select machines..."}
                                                                            error={!!validationErrors[`${rowKey}_machine`]}
                                                                            helperText={validationErrors[`${rowKey}_machine`] || ""}
                                                                            InputProps={{
                                                                                ...params.InputProps,
                                                                                endAdornment: (
                                                                                    <>
                                                                                        {machinesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                                                        {params.InputProps.endAdornment}
                                                                                    </>
                                                                                ),
                                                                            }}
                                                                        />
                                                                    )}
                                                                />
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}

                            {uploadTabIndex === 1 && isEditing && newDoc.attachment_type === "file" && (
                                <Box sx={{ mt: 2, border: "1px solid #e9edf2", borderRadius: 1.5, overflow: "hidden" }}>
                                                    <Box
                                                        sx={{
                                                            display: "grid",
                                                            gridTemplateColumns: `${uploadRowIconColumn} ${uploadRowNameColumn} ${uploadRowCategoryColumn} ${uploadRowStatusColumn} ${uploadRowMachineColumn}`,
                                                            gap: 1.25,
                                                            px: 1.5,
                                                            py: 1,
                                                            backgroundColor: "#f7f9fb",
                                                            borderBottom: "1px solid #e9edf2",
                                                            fontWeight: 700,
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <span />
                                                        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Name</RequiredLabel></Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Category</RequiredLabel></Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}>Status</Typography>
                                                        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 0 }}><RequiredLabel>Machine</RequiredLabel></Typography>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: "grid",
                                                            gridTemplateColumns: `${uploadRowIconColumn} ${uploadRowNameColumn} ${uploadRowCategoryColumn} ${uploadRowStatusColumn} ${uploadRowMachineColumn}`,
                                                            gap: 1.25,
                                                            px: 1.5,
                                                            py: 1.25,
                                                            alignItems: "center",
                                                            borderBottom: "1px solid #eef2f6",
                                                            backgroundColor: "#fff",
                                                        }}
                                                    >
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                                            <Box
                                                                sx={{
                                                                    width: 34,
                                                                    height: 34,
                                                                    borderRadius: 1,
                                                                    ...getFileTypeBadgeColors(newDoc.name ?? editingDocument?.name ?? ""),
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    fontWeight: 700,
                                                                    fontSize: getFileTypeBadgeFontSize(getFileTypeBadgeLabel(newDoc.name ?? editingDocument?.name ?? "")),
                                                                    lineHeight: 1,
                                                                    letterSpacing: 0.2,
                                                                    flex: "0 0 auto",
                                                                    textTransform: "uppercase",
                                                                    px: 0.35,
                                                                }}
                                                            >
                                                                {getFileTypeBadgeLabel(newDoc.name ?? editingDocument?.name ?? "")}
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {newDoc.name ?? editingDocument?.name ?? getFileBaseName(editingDocument?.name || "")}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {(selectedDocCategories || [])
                                                                    .map((category) => category?.label || category?.category_name || "")
                                                                    .filter(Boolean)
                                                                    .join(", ") || "Uncategorized"}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {(newDoc.status || "private") === "public" ? "Public" : "Private"}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ minWidth: 0, width: "100%" }}>
                                                            <Autocomplete
                                                                multiple
                                                                size="small"
                                                                options={machines}
                                                                disablePortal
                                                                slotProps={getMachineAutocompleteSlotProps(0, 1)}
                                                                filterOptions={(x) => x}
                                                                getOptionLabel={(machine) => `${machine.machine_number} - ${machine.machine_name}`}
                                                                isOptionEqualToValue={(option, value) => option.machine_id === value.machine_id}
                                                                value={selectedMachines}
                                                                onChange={(event, newValue) => {
                                                                    setSelectedMachines(newValue || []);
                                                                    setValidationErrors((prev) => {
                                                                        const next = { ...prev };
                                                                        delete next.machine;
                                                                        return next;
                                                                    });
                                                                }}
                                                                onInputChange={(e, value) => {
                                                                    setMachineSearch(value);
                                                                    clearTimeout(window.__md_search_timer__);
                                                                    window.__md_search_timer__ = setTimeout(() => {
                                                                        fetchMachineResults(1, value || "");
                                                                    }, 300);
                                                                }}
                                                                sx={bulkMultiSelectSx}
                                                                renderTags={(value, getTagProps) =>
                                                                    renderScrollableTags(
                                                                        value,
                                                                        getTagProps,
                                                                        (machine) => `${machine.machine_number} - ${machine.machine_name}`
                                                                    )
                                                                }
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        fullWidth
                                                                        placeholder={selectedMachines.length > 0 ? "" : "Select machines..."}
                                                                        error={!!validationErrors.machine}
                                                                        helperText={validationErrors.machine || ""}
                                                                        InputProps={{
                                                                            ...params.InputProps,
                                                                            endAdornment: (
                                                                                <>
                                                                                    {machinesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                                                                    {params.InputProps.endAdornment}
                                                                                </>
                                                                            ),
                                                                        }}
                                                                    />
                                                                )}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                    {uploadTabIndex === 2 && (
                                        <Box
                                            sx={{
                                                mt: 2,
                                                p: 2,
                                                border: "1px solid #dbe7f4",
                                                borderRadius: 1.5,
                                                backgroundColor: "#f7fbff",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", mb: 1.25 }}>
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                        Upload Progress
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {uploadPhase === "finalizing"
                                                            ? "Saving document records..."
                                                            : uploadPhase === "done"
                                                                ? "Upload completed"
                                                                : "Uploading documents..."}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#0b9bd3" }}>
                                                    {uploadOverallProgress}%
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={uploadOverallProgress}
                                                sx={{ height: 8, borderRadius: 999, mb: 2, backgroundColor: "#dbe7f4" }}
                                            />
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                {uploadProgressItems.map((progressItem) => {
                                                    const fileKey = progressItem.key;
                                                    const percent = Number(progressItem.percent || 0);
                                                    const fileName = progressItem.name || "Document";
                                                    const statusLabel = (() => {
                                                        switch (progressItem.status) {
                                                            case "uploaded":
                                                                return "Uploaded";
                                                            case "completed":
                                                                return "Completed";
                                                            case "failed":
                                                                return "Failed";
                                                            case "uploading":
                                                                return "Uploading";
                                                            case "preparing":
                                                                return "Preparing";
                                                            default:
                                                                return "Queued";
                                                        }
                                                    })();
                                                    return (
                                                        <Box
                                                            key={`${fileKey}-progress`}
                                                            sx={{
                                                                p: 1.25,
                                                                borderRadius: 1.25,
                                                                backgroundColor: "#fff",
                                                                border: "1px solid #e5edf6",
                                                            }}
                                                        >
                                                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center", mb: 0.75 }}>
                                                                <Box sx={{ minWidth: 0 }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                        {fileName}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {progressItem.message || statusLabel}
                                                                    </Typography>
                                                                </Box>
                                                                <Chip size="small" label={`${percent}%`} sx={{ fontWeight: 700 }} />
                                                            </Box>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={percent}
                                                                sx={{ height: 6, borderRadius: 999, backgroundColor: "#edf3f9" }}
                                                            />
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    )}
                                    {error.file && <FormHelperText error sx={{ mt: 0.5 }}>{error.file}</FormHelperText>}
                                </Box>
                            )}

                        </Box>
                    </Box>
                }
                actions={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap' ,width:'100%'}}>
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'nowrap', minWidth: 0, }}>
                         {selectedDocumentFiles.length > 0 && (
                                <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                                    {selectedDocumentFiles.length} file{selectedDocumentFiles.length === 1 ? "" : "s"} - {formatFileSize(totalSelectedFileSize)}
                                </Typography>
                            )} </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', columnGap: 1.5, flexWrap: 'nowrap', flexShrink: 0, whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'nowrap', minWidth: 0 }}>
                           
                            {uploadTabIndex !== 2 && !isEditing && (
                                <>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            whiteSpace: 'normal',
                                            maxWidth: 230,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        Send email notification after batch completion. Disable to skip notification.
                                    </Typography>
                                    <FormControlLabel
                                        sx={{ m: 0 }}
                                        label={<Typography variant="body2">Notify</Typography>}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={uploadNotificationsEnabled}
                                                onChange={(e) => setUploadNotificationsEnabled(e.target.checked)}
                                            />
                                        }
                                    />
                                </>
                            )}
                        </Box>
                            {uploadTabIndex !== 2 ? (
                                <Button variant="outlined" onClick={handleCloseModal} color="primary" disabled={isSubmittingDocument}>
                                    {t('BUTTON.CANCEL')}
                                </Button>
                            ) : null}
                            {uploadTabIndex === 0 ? (
                                <Button
                                    onClick={() => {
                                        if (validateStepOne()) {
                                            setUploadTabIndex(1);
                                        }
                                    }}
                                    color="primary"
                                    variant="contained"
                                >
                                    Next
                                </Button>
                            ) : uploadTabIndex === 1 ? (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap' }}>
                                    <Button
                                        onClick={() => setUploadTabIndex(0)}
                                        variant="outlined"
                                        color="error"
                                        disabled={isSubmittingDocument}
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        onClick={handleStartDocumentUpload}
                                        color="primary"
                                        variant="contained"
                                        disabled={isSubmittingDocument}
                                        startIcon={isSubmittingDocument ? <CircularProgress size={16} /> : undefined}
                                    >
                                        {isEditing ? (t('BUTTON.UPDATE') || 'Update') : 'Upload Documents'}
                                    </Button>
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                }
            />

            <CategoryViewModal
                open={categoryDetailsModal.open}
                category={categoryDetailsModal.category}
                documents={categoryDetailsModal.documents}
                onClose={handleCloseCategoryDetails}
                onViewDocument={handleViewDocument}
                onRemoveDocument={(doc) =>
                    handleRemoveDocumentFromCategory(categoryDetailsModal.category?.id, doc.id)
                }
                onAddDocument={handleAddDocumentFromCategory}
                onAssignDocuments={handleOpenAssignModal}
                onDeleteCategory={handleDeleteCategoryRequest}
            />

            <DeleteCategoryModal
                open={deleteCategoryModalState.open}
                category={deleteCategoryModalState.category}
                categories={scopedCategories}
                exclusiveDocuments={exclusiveDocsForDelete}
                categoryDocuments={categoryDocumentsForDelete}
                defaultCategory={defaultCategory}
                onClose={handleCloseDeleteCategoryModal}
                onConfirm={handleConfirmDeleteCategory}
            />

            <AddCategoryModal
                open={addCategoryModalOpen}
                onClose={() => setAddCategoryModalOpen(false)}
                onSubmit={handleCreateCategory}
            />

            <EditCategoryModal
                open={editCategoryModal.open}
                category={editCategoryModal.category}
                onClose={() => setEditCategoryModal({ open: false, category: null })}
                onSubmit={handleEditCategorySubmit}
            />

            <ModalWrapper
                open={assignModal.open}
                setOpen={(open) => {
                    if (!open) handleAssignModalClose();
                }}
                onClose={handleAssignModalClose}
                title={`${t('LABELS.CATEGORY') || 'Category'}: ${assignModal.category?.category_name || ''}`}
                content={
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1, width: '100%' }}>
                        <Autocomplete
                            multiple
                            options={documents}
                            getOptionLabel={(option) => option.name || option.document_name || ''}
                            value={assignSelectedDocs}
                            onChange={handleAssignDocumentsChange}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip {...getTagProps({ index })} key={`${option.id}-${index}`} label={option.name || option.document_name || ''} />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField {...params} label={t('LABELS.DOCUMENTS') || 'Documents'} size="small" />
                            )}
                        />
                    </Box>
                }
                actions={
                    <>
                        <Button onClick={handleAssignModalClose}>{t('BUTTON.CANCEL')}</Button>
                        <Button variant="contained" onClick={handleAssignDocumentsSave}>{t('BUTTON.SAVE') || 'Save'}</Button>
                    </>
                }
            />

            <DocumentViewModal
                open={viewModalOpen}
                setOpen={setViewModalOpen}
                document={viewDoc}
                onClose={handleCloseViewDocument}
                onEdit={
                    canEditDocuments && viewDoc
                        ? () => {
                            handleCloseViewDocument();
                            handleEditOpen(viewDoc);
                        }
                        : undefined
                }
                onDelete={canDeleteDocuments && viewDoc ? () => handleDeleteDocument(viewDoc.id) : undefined}
            />
        </div>
    );
};

export default ManageDocuments;
